/*
 * Copyright 2020 The Yorkie Authors. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { DocEventType as PbDocEventType } from '@yorkie-js/sdk/src/api/yorkie/v1/resources_pb';
import { converter } from '@yorkie-js/sdk/src/api/converter';
import { logger, LogLevel } from '@yorkie-js/sdk/src/util/logger';
import {
  ChangeApplyError,
  Code,
  YorkieError,
} from '@yorkie-js/sdk/src/util/error';
import { deepcopy } from '@yorkie-js/sdk/src/util/object';
import { DocSize, totalDocSize } from '@yorkie-js/sdk/src/util/resource';
import {
  Observer,
  Observable,
  createObservable,
  Unsubscribe,
  ErrorFn,
  CompleteFn,
  NextFn,
} from '@yorkie-js/sdk/src/util/observable';
import {
  ActorID,
  InitialActorID,
} from '@yorkie-js/sdk/src/document/time/actor_id';
import { Attachable } from '@yorkie-js/sdk/src/client/attachable';
import { VersionVector } from '@yorkie-js/sdk/src/document/time/version_vector';
import {
  Change,
  ChangeStruct,
} from '@yorkie-js/sdk/src/document/change/change';
import {
  ChangeID,
  InitialChangeID,
} from '@yorkie-js/sdk/src/document/change/change_id';
import { ChangeContext } from '@yorkie-js/sdk/src/document/change/context';
import { ChangePack } from '@yorkie-js/sdk/src/document/change/change_pack';
import {
  Checkpoint,
  InitialCheckpoint,
} from '@yorkie-js/sdk/src/document/change/checkpoint';
import {
  OpSource,
  OpInfo,
  ObjectOpInfo,
  TextOpInfo,
  CounterOpInfo,
  ArrayOpInfo,
  TreeOpInfo,
  Operation,
} from '@yorkie-js/sdk/src/document/operation/operation';
import { ArraySetOperation } from '@yorkie-js/sdk/src/document/operation/array_set_operation';
import { AddOperation } from '@yorkie-js/sdk/src/document/operation/add_operation';
import { SetOperation } from '@yorkie-js/sdk/src/document/operation/set_operation';
import { dropSplitLinksInElement } from '@yorkie-js/sdk/src/document/crdt/split_links';
import {
  createJSON,
  JSONElement,
} from '@yorkie-js/sdk/src/document/json/element';
import { JSONArray } from '@yorkie-js/sdk/src/document/json/array';
import { JSONObject } from '@yorkie-js/sdk/src/document/json/object';
import { Counter } from '@yorkie-js/sdk/src/document/json/counter';
import { Text } from '@yorkie-js/sdk/src/document/json/text';
import { Tree } from '@yorkie-js/sdk/src/document/json/tree';
import { CRDTRoot, RootStats } from '@yorkie-js/sdk/src/document/crdt/root';
import { CRDTObject } from '@yorkie-js/sdk/src/document/crdt/object';
import { Channel } from '@yorkie-js/sdk/src/document/presence/presence';
import { PresenceChangeType } from '@yorkie-js/sdk/src/document/presence/change';
import {
  History,
  HistoryOperation,
  MaxUndoRedoStackDepth,
} from '@yorkie-js/sdk/src/document/history';
import {
  Primitive,
  PrimitiveValue,
} from '@yorkie-js/sdk/src/document/crdt/primitive';
import { Rule } from '@yorkie-js/schema';
import { validateYorkieRuleset } from '@yorkie-js/sdk/src/document/schema/ruleset_validator';
import { setupDevtools } from '@yorkie-js/sdk/src/devtools';
import * as Devtools from '@yorkie-js/sdk/src/devtools/types';
import { EditOperation } from './operation/edit_operation';
import { TreeEditOperation } from './operation/tree_edit_operation';
import { TreeStyleOperation } from './operation/tree_style_operation';
import { CRDTTreeNodeID } from './crdt/tree';

/**
 * `DocumentOptions` are the options to create a new document.
 */
export interface DocumentOptions {
  /**
   * `disableGC` disables garbage collection if true.
   */
  disableGC?: boolean;

  /**
   * `disablePresence` declares that this document does not use presence.
   * When true, `Document.update`'s `presence.set` calls are silently
   * dropped and the server strips any presence that nonetheless reaches
   * it. The option is server-fixated on first attach — once a document
   * is created presenceless, subsequent attaches observe `true` from the
   * attach response regardless of the local option value. Use for
   * counter-only or other presence-free workloads where the per-client
   * presence map would otherwise leak memory in long-lived documents.
   */
  disablePresence?: boolean;

  /**
   * `enableDevtools` enables devtools if true.
   */
  enableDevtools?: boolean;

  /**
   * `maxUndoDepth` is how many changes `history.undo()` can go back, and
   * likewise for `history.redo()`. Each `Document.update` that changes the
   * document is one entry; the oldest is dropped when the stack is full.
   * Defaults to `MaxUndoRedoStackDepth` (50). Must be a positive integer.
   * A reverse entry keeps a copy of the content it would restore, so a
   * larger depth holds more memory.
   */
  maxUndoDepth?: number;
}

/**
 * `DocStatus` represents the status of the document.
 */
export enum DocStatus {
  /**
   * Detached means that the document is not attached to the client.
   * The actor of the ticket is created without being assigned.
   */
  Detached = 'detached',
  /**
   * Attached means that this document is attached to the client.
   * The actor of the ticket is created with being assigned by the client.
   */
  Attached = 'attached',
  /**
   * Removed means that this document is removed. If the document is removed,
   * it cannot be edited.
   */
  Removed = 'removed',
}

/**
 * `DocEventType` represents the type of the event that occurs in `Document`.
 */
export enum DocEventType {
  /**
   * status changed event type
   */
  StatusChanged = 'status-changed',

  /**
   * `ConnectionChanged` means that the watch stream connection status has changed.
   */
  ConnectionChanged = 'connection-changed',

  /**
   * `SyncStatusChanged` means that the document sync status has changed.
   */
  SyncStatusChanged = 'sync-status-changed',

  /**
   * snapshot event type
   */
  Snapshot = 'snapshot',

  /**
   * local document change event type
   */
  LocalChange = 'local-change',

  /**
   * remote document change event type
   */
  RemoteChange = 'remote-change',

  /**
   * `Initialized` means that online clients have been loaded from the server.
   */
  Initialized = 'initialized',

  /**
   * `Watched` means that the client has established a connection with the server,
   * enabling real-time synchronization.
   */
  Watched = 'watched',

  /**
   * `Unwatched` means that the connection has been disconnected.
   */
  Unwatched = 'unwatched',

  /**
   * `PresenceChanged` means that the presences of the client has updated.
   */
  PresenceChanged = 'presence-changed',

  /**
   * `AuthError` indicates an authorization failure in syncLoop or watchLoop.
   */
  AuthError = 'auth-error',

  /**
   * `EpochMismatch` indicates the document was compacted on the server
   * and this client must detach and reattach to recover.
   */
  EpochMismatch = 'epoch-mismatch',

  /**
   * `LocalChangesDropped` indicates the offline-persistence layer had to
   * discard un-pushed local changes it could not reconcile with the server
   * (a stale-epoch re-anchor, a server-side GC/purge of the document, or a
   * store reused under a different actor). The event carries the dropped
   * changes so the app can surface the data loss and, if it wishes, re-apply
   * them on top of the re-anchored state.
   */
  LocalChangesDropped = 'local-changes-dropped',

  /**
   * `PersistDisabled` indicates the offline-persistence layer stopped
   * persisting this document because writing it costs more than the configured
   * budget allows. Editing continues; durability does not. An app that reports
   * sync state should say so, since the user's work is no longer being kept.
   */
  PersistDisabled = 'persist-disabled',
}

/**
 * `DocEvent` is an event that occurs in `Document`. It can be delivered
 * using `Document.subscribe()`.
 */
export type DocEvent<P extends Indexable = Indexable, T = OpInfo> =
  | StatusChangedEvent
  | ConnectionChangedEvent
  | SyncStatusChangedEvent
  | SnapshotEvent
  | LocalChangeEvent<T, P>
  | RemoteChangeEvent<T, P>
  | PresenceEvent<P>
  | AuthErrorEvent
  | EpochMismatchEvent
  | LocalChangesDroppedEvent<P>
  | PersistDisabledEvent;

/**
 * `DocEvents` represents document events that occur within
 * a single transaction (e.g., doc.update).
 */
export type DocEvents<P extends Indexable = Indexable> = Array<DocEvent<P>>;

interface BaseDocEvent {
  type: DocEventType;
}

/**
 * `StatusChangedEvent` is an event that occurs when the status of a document changes.
 */
export interface StatusChangedEvent extends BaseDocEvent {
  type: DocEventType.StatusChanged;
  source: OpSource;
  value:
    | { status: DocStatus.Attached; actorID: string }
    | { status: DocStatus.Detached }
    | { status: DocStatus.Removed };
}

/**
 * `StreamConnectionStatus` represents whether the stream connection is connected or not.
 */
export enum StreamConnectionStatus {
  /**
   * `Connected` means that the stream connection is connected.
   */
  Connected = 'connected',

  /**
   * `Disconnected` means that the stream connection is disconnected.
   */
  Disconnected = 'disconnected',
}

/**
 * `ConnectionChangedEvent` is an event that occurs when the stream connection state changes.
 */
export interface ConnectionChangedEvent extends BaseDocEvent {
  type: DocEventType.ConnectionChanged;
  value: StreamConnectionStatus;
}

/**
 * `DocSyncStatus` represents the result of synchronizing the document with the server.
 */
export enum DocSyncStatus {
  /**
   * `Synced` means that document synced successfully.
   */
  Synced = 'synced',

  /**
   * `SyncFiled` means that document synchronization has failed.
   */
  SyncFailed = 'sync-failed',
}

/**
 * `SyncStatusChangedEvent` is an event that occurs when document is synced with the server.
 */
export interface SyncStatusChangedEvent extends BaseDocEvent {
  type: DocEventType.SyncStatusChanged;
  value: DocSyncStatus;
}

/**
 * `SnapshotEvent` is an event that occurs when a snapshot is received from
 * the server.
 */
export interface SnapshotEvent extends BaseDocEvent {
  type: DocEventType.Snapshot;
  source: OpSource.Remote;
  value: {
    snapshot: string | undefined;
    serverSeq: string;
    snapshotVector: string;
  };
}

/**
 * `ChangeInfo` represents the modifications made during a document update
 * and the message passed.
 */
export interface ChangeInfo<T = OpInfo> {
  message: string;
  operations: Array<T>;
  actor: ActorID;
  clientSeq: number;
  serverSeq: string;
}

/**
 * `PresenceEvent` is an event that occurs when the presence of a client changes.
 */
export type PresenceEvent<P extends Indexable = Indexable> =
  | InitializedEvent<P>
  | WatchedEvent<P>
  | UnwatchedEvent<P>
  | PresenceChangedEvent<P>;

/**
 * `LocalChangeEvent` is an event that occurs when the document is changed
 * by local changes.
 */
export interface LocalChangeEvent<
  T = OpInfo,
  P extends Indexable = Indexable,
> extends BaseDocEvent {
  type: DocEventType.LocalChange;
  source: OpSource.Local | OpSource.UndoRedo;
  value: ChangeInfo<T>;
  rawChange?: ChangeStruct<P>;
}

/**
 * `RemoteChangeEvent` is an event that occurs when the document is changed
 * by remote changes.
 */
export interface RemoteChangeEvent<
  T = OpInfo,
  P extends Indexable = Indexable,
> extends BaseDocEvent {
  type: DocEventType.RemoteChange;
  source: OpSource.Remote;
  value: ChangeInfo<T>;
  rawChange?: ChangeStruct<P>;
}

export interface InitializedEvent<P extends Indexable> extends BaseDocEvent {
  type: DocEventType.Initialized;
  source: OpSource.Local;
  value: Array<{ clientID: ActorID; presence: P }>;
}

export interface WatchedEvent<P extends Indexable> extends BaseDocEvent {
  type: DocEventType.Watched;
  source: OpSource.Remote;
  value: { clientID: ActorID; presence: P };
}

export interface UnwatchedEvent<P extends Indexable> extends BaseDocEvent {
  type: DocEventType.Unwatched;
  source: OpSource.Remote;
  value: { clientID: ActorID; presence: P };
}

export interface PresenceChangedEvent<
  P extends Indexable,
> extends BaseDocEvent {
  type: DocEventType.PresenceChanged;
  source: OpSource;
  value: { clientID: ActorID; presence: P };
}

export interface AuthErrorEvent extends BaseDocEvent {
  type: DocEventType.AuthError;
  value: {
    reason: string;
    method: 'PushPull' | 'Watch';
  };
}

export interface EpochMismatchEvent extends BaseDocEvent {
  type: DocEventType.EpochMismatch;
  value: {
    method: 'PushPull';
  };
}

export type PersistDisabledReason =
  // Serializing the document exceeded `maxPersistBytes`.
  | 'too-large'
  // Serializing the document took longer than `maxPersistMillis`.
  | 'too-slow';

/**
 * `PersistDisabledEvent` reports that this document is no longer being
 * persisted. It fires once per attachment: the measurement that triggers it is
 * taken at compaction, and latching tears the persist subscription down, so
 * the cost of discovering the document is unaffordable is paid once.
 */
export interface PersistDisabledEvent extends BaseDocEvent {
  type: DocEventType.PersistDisabled;
  value: { reason: PersistDisabledReason; bytes: number; millis: number };
}

/**
 * `LocalChangesDroppedReason` enumerates why the offline-persistence layer
 * had to discard un-pushed local changes it could not reconcile.
 */
export type LocalChangesDroppedReason =
  | 'epoch-reanchor'
  | 'document-purged'
  | 'actor-mismatch'
  | 'restore-failed'
  // The persisted change log had a `clientSeq` hole — an append that never
  // landed — so it could not be replayed: the server rejects a discontinuous
  // run, and a document restored from one would never sync again.
  | 'log-discontinuity';

/**
 * `LocalChangesDroppedEvent` is an app-visible data-loss signal: the persisted
 * un-pushed local changes could not be reconciled with the server and were
 * discarded. `value.changes` carries their serialized structs so the app can
 * surface the loss and optionally re-apply them on top of the recovered state.
 */
export interface LocalChangesDroppedEvent<
  P extends Indexable = Indexable,
> extends BaseDocEvent {
  type: DocEventType.LocalChangesDropped;
  value: {
    reason: LocalChangesDroppedReason;
    changes: Array<ChangeStruct<P>>;
  };
}

type DocEventCallbackMap<P extends Indexable> = {
  default: NextFn<
    LocalChangeEvent<OpInfo, P> | RemoteChangeEvent<OpInfo, P> | SnapshotEvent
  >;
  presence: NextFn<PresenceEvent<P>>;
  'my-presence': NextFn<InitializedEvent<P> | PresenceChangedEvent<P>>;
  others: NextFn<WatchedEvent<P> | UnwatchedEvent<P> | PresenceChangedEvent<P>>;
  connection: NextFn<ConnectionChangedEvent>;
  status: NextFn<StatusChangedEvent>;
  sync: NextFn<SyncStatusChangedEvent>;
  'auth-error': NextFn<AuthErrorEvent>;
  'epoch-mismatch': NextFn<EpochMismatchEvent>;
  'local-changes-dropped': NextFn<LocalChangesDroppedEvent<P>>;
  'persist-disabled': NextFn<PersistDisabledEvent>;
  all: NextFn<DocEvents<P>>;
};
export type DocEventTopic = keyof DocEventCallbackMap<never>;
export type DocEventCallback<P extends Indexable> =
  DocEventCallbackMap<P>[DocEventTopic];

/**
 * `Json` represents the JSON data type. It is used to represent the data
 * structure of the document.
 */
export type Json = JsonPrimitive | JsonArray | JsonObject;

// eslint-disable-next-line @typescript-eslint/no-restricted-types
type JsonPrimitive = string | number | boolean | null;
type JsonArray = Array<Json>;
type JsonObject = { [key: string]: Json | undefined };

/**
 * `Indexable` represents an object with string keys and Json values. It is
 * used to various places such as the presence or attributes of text elements
 * and etc.
 */
export type Indexable = Record<string, Json>;

export type LeafElement = PrimitiveValue | Primitive | Text | Counter | Tree;
export type BaseArray<T> = JSONArray<T> | Array<T>;
export type BaseObject<T> = JSONObject<T> | T;

/**
 * `OpInfoOfElement` represents the type of the operation info of the given element.
 */
type OpInfoOfElement<TElem> = TElem extends Text
  ? TextOpInfo
  : TElem extends Counter
    ? CounterOpInfo
    : TElem extends Tree
      ? TreeOpInfo
      : TElem extends BaseArray<any>
        ? ArrayOpInfo
        : TElem extends BaseObject<any>
          ? ObjectOpInfo
          : OpInfo;

/**
 * `OpInfoOfInner` represents the type of the operation info of the
 * given path in the Document.subscribe.
 */
type OpInfoOfInner<
  TElem,
  TKeyOrPath,
  TDepth extends number = 0,
> = TDepth extends 0
  ? OpInfoOfElement<TElem>
  : TKeyOrPath extends `${infer TFirst}.${infer TRest}`
    ? TFirst extends keyof TElem
      ? OpInfoOfInner<TElem[TFirst], TRest, DecreasedDepthOf<TDepth>>
      : OpInfo
    : TKeyOrPath extends keyof TElem
      ? OpInfoOfElement<TElem[TKeyOrPath]>
      : OpInfo;

/**
 * `DecreasedDepthOf` represents the type of the decreased depth of the given depth.
 */
type DecreasedDepthOf<Depth extends number> = Depth extends 10
  ? 9
  : Depth extends 9
    ? 8
    : Depth extends 8
      ? 7
      : Depth extends 7
        ? 6
        : Depth extends 6
          ? 5
          : Depth extends 5
            ? 4
            : Depth extends 4
              ? 3
              : Depth extends 3
                ? 2
                : Depth extends 2
                  ? 1
                  : Depth extends 1
                    ? 0
                    : -1;

/**
 * `PathOfInner` represents the type of the path of the given element.
 */
type PathOfInner<
  TElem,
  Prefix extends string = '',
  Depth extends number = 0,
> = Depth extends 0
  ? Prefix
  : TElem extends Record<string, any>
    ? {
        [TKey in keyof TElem]: TElem[TKey] extends LeafElement
          ? `${Prefix}${TKey & string}`
          : TElem[TKey] extends BaseArray<infer TArrayElement>
            ?
                | `${Prefix}${TKey & string}`
                | `${Prefix}${TKey & string}.${number}`
                | PathOfInner<
                    TArrayElement,
                    `${Prefix}${TKey & string}.${number}.`,
                    DecreasedDepthOf<Depth>
                  >
            :
                | `${Prefix}${TKey & string}`
                | PathOfInner<
                    TElem[TKey],
                    `${Prefix}${TKey & string}.`,
                    DecreasedDepthOf<Depth>
                  >;
      }[keyof TElem]
    : Prefix extends `${infer TRest}.`
      ? TRest
      : Prefix;

/**
 * `OpInfoOf` represents the type of the operation info of the given
 * path in the Document.subscribe. It is used to remove the `$.` prefix.
 */
type OpInfoOf<
  TRoot,
  TKey extends string = '',
  TDepth extends number = 10,
> = TKey extends `$.${infer TPath}`
  ? OpInfoOfInner<TRoot, TPath, TDepth>
  : OpInfoOfInner<TRoot, TKey, TDepth>;

/**
 * `PathOf` represents the type of all possible paths in the Document.subscribe.
 */
type PathOf<TRoot, Depth extends number = 10> = PathOfInner<TRoot, '$.', Depth>;

/**
 * `packBlobs` concatenates the given byte blobs into a single, self-contained
 * envelope. Each blob is prefixed with its length as a 4-byte little-endian
 * uint32, so `unpackBlobs` can split them back out without a shared schema.
 * This avoids adding a new message to the generated protobuf api, whose source
 * of truth lives in the yorkie server repo.
 */
function packBlobs(blobs: Array<Uint8Array>): Uint8Array {
  let total = 0;
  for (const blob of blobs) {
    total += 4 + blob.length;
  }

  const out = new Uint8Array(total);
  const view = new DataView(out.buffer);
  let offset = 0;
  for (const blob of blobs) {
    view.setUint32(offset, blob.length, true);
    offset += 4;
    out.set(blob, offset);
    offset += blob.length;
  }
  return out;
}

/**
 * `unpackBlobs` is the reverse of `packBlobs`: it splits the given envelope
 * back into the original ordered list of byte blobs.
 */
function unpackBlobs(bytes: Uint8Array): Array<Uint8Array> {
  const blobs: Array<Uint8Array> = [];
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset = 0;
  while (offset < bytes.length) {
    if (offset + 4 > bytes.length) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'corrupt envelope: truncated length prefix',
      );
    }
    const len = view.getUint32(offset, true);
    offset += 4;
    if (offset + len > bytes.length) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'corrupt envelope: blob length exceeds remaining bytes',
      );
    }
    blobs.push(bytes.subarray(offset, offset + len));
    offset += len;
  }
  return blobs;
}

/**
 * `Document` is a CRDT-based data type. We can represent the model
 * of the application and edit it even while offline.
 * It implements Attachable interface to be managed by Attachment.
 */
export class Document<
  R,
  P extends Indexable = Indexable,
> implements Attachable {
  private key: string;
  private status: DocStatus;
  private opts: DocumentOptions;
  private maxSizeLimit: number;
  private schemaRules: Array<Rule>;

  private changeID: ChangeID;
  private checkpoint: Checkpoint;
  private localChanges: Array<Change<P>>;

  // `epoch` is the document's last-known compaction epoch (proto int64). The
  // client learns it from every server response pack (`applyChangePack`) and
  // presents it back on the next attach/sync (`createChangePack`). A resumed
  // offline client persists it (`toBytes`) so, after a reload, the attach
  // presents the epoch the document was last synced under; if the document was
  // force-compacted while offline the server sees a stale epoch and rejects the
  // resume with `ErrEpochMismatch`, driving the store-backed re-anchor.
  private epoch: bigint;

  // `docID` is the server-assigned document id (`AttachDocumentResponse.
  // documentId`) recorded by the client after a successful attach. It is
  // persisted in the `toBytes` envelope so a restored offline session can
  // compare it against the id the server returns on re-attach: if they differ
  // the server GC'd/deleted the document and minted a fresh one, which would
  // otherwise silently drop the local snapshot + edits (Tier-3 guard). Empty
  // until the first attach records it (and for legacy envelopes without it).
  private docID: string;

  private root: CRDTRoot;
  private presences: Map<ActorID, P>;
  private clone?: { root: CRDTRoot; presences: Map<ActorID, P> };
  private internalHistory: History<P>;
  private isUpdating: boolean;
  private onlineClients: Set<ActorID>;

  private eventStream: Observable<DocEvents<P>>;
  private eventStreamObserver!: Observer<DocEvents<P>>;

  // Persistence follows the pending queue, including changes with no public
  // event. Keep this signal separate from editor and presence subscriptions.
  private localChangeStream: Observable<void>;
  private localChangeObserver!: Observer<void>;

  // `disableGC`, when true, declares that this document does not produce or
  // consume tombstones (see disable-gc-on-attach in the server repo). It is
  // set by the client on Attach and consumed by applyChange to skip merging
  // remote actors' version vectors into changeID, keeping each subsequent
  // local Change's VV at O(1) for high-fan-out Counter workloads.
  private disableGC: boolean;

  // `disablePresence`, when true, declares that this document does not use
  // presence. The client sets it from the local option on construction and
  // overwrites it from the server-fixated value in the attach response. While
  // true, `Document.update`'s `presence.set` calls are silently dropped on
  // the way out (server enforcement is authoritative for the wire side).
  private disablePresence: boolean;

  // `presenceDropWarned` ensures the silent-drop warning emitted by
  // `Document.update` for opt-in documents fires only once per document
  // lifetime. Otherwise long-lived counter-only documents would spam the
  // console on every well-meaning `presence.set` call.
  private presenceDropWarned: boolean;

  /**
   * `history` is exposed to the user to manage undo/redo operations.
   */
  public history;

  constructor(key: string, opts?: DocumentOptions) {
    this.key = key;
    this.status = DocStatus.Detached;
    this.opts = opts || {};
    this.maxSizeLimit = 0;
    this.schemaRules = [];

    this.changeID = InitialChangeID;
    this.checkpoint = InitialCheckpoint;
    this.localChanges = [];
    this.epoch = 0n;
    this.docID = '';
    this.disableGC = false;
    // Seed from the local option so the gate already works before the first
    // attach response lands (e.g. a unit test that constructs a Document with
    // `{ disablePresence: true }` and calls `update` immediately).
    this.disablePresence = !!opts?.disablePresence;
    this.presenceDropWarned = false;

    this.root = CRDTRoot.create();
    this.presences = new Map();
    this.onlineClients = new Set();
    const maxUndoDepth = opts?.maxUndoDepth ?? MaxUndoRedoStackDepth;
    if (!Number.isInteger(maxUndoDepth) || maxUndoDepth < 1) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        `maxUndoDepth must be a positive integer: ${maxUndoDepth}`,
      );
    }
    this.internalHistory = new History(maxUndoDepth);
    this.isUpdating = false;

    this.eventStream = createObservable<DocEvents<P>>(
      (observer) => (this.eventStreamObserver = observer),
    );

    this.localChangeStream = createObservable<void>(
      (observer) => (this.localChangeObserver = observer),
    );

    this.history = {
      canUndo: () => this.internalHistory.hasUndo() && !this.isUpdating,
      canRedo: () => this.internalHistory.hasRedo() && !this.isUpdating,
      undo: () => this.executeUndoRedo(true),
      redo: () => this.executeUndoRedo(false),
    };

    setupDevtools(this);
  }

  /**
   * `update` executes the given updater to update this document.
   */
  public update(
    updater: (root: JSONObject<R>, presence: Channel<P>) => void,
    message?: string,
  ): void {
    if (this.getStatus() === DocStatus.Removed) {
      throw new YorkieError(Code.ErrDocumentRemoved, `${this.key} is removed`);
    }

    // 01. Update the clone object and create a change.
    this.ensureClone();
    const actorID = this.changeID.getActorID();
    const ctx = ChangeContext.create<P>(
      this.changeID,
      this.clone!.root,
      this.clone!.presences.get(actorID) || ({} as P),
      message,
    );

    try {
      const proxy = createJSON<JSONObject<R>>(
        ctx,
        this.clone!.root.getObject(),
      );

      if (!this.presences.has(actorID)) {
        this.clone!.presences.set(actorID, {} as P);
      }

      // NOTE(hackerwins): The updater should not be able to call undo/redo.
      // If the updater calls undo/redo, an error will be thrown.
      this.isUpdating = true;
      updater(proxy, new Channel(ctx, this.clone!.presences.get(actorID)!));
    } catch (err) {
      // NOTE(hackerwins): If the updater fails, we need to remove the cloneRoot and
      // clonePresences to prevent the user from accessing the invalid state.
      this.clone = undefined;

      throw err;
    } finally {
      this.isUpdating = false;
    }

    // Silently drop any presence change recorded by the updater when the
    // document was attached presenceless. The server is authoritative —
    // it strips presence on PushPull entry and never emits presence on
    // watch events for these documents — but dropping locally avoids the
    // wasted enqueue, a redundant LocalChange event with no payload, and
    // a stale history entry. The warning is emitted at most once per
    // document instance so high-frequency callers don't spam the console.
    if (this.disablePresence && ctx.hasPresenceChange()) {
      ctx.dropPresenceChange();
      ctx.clearReversePresence();
      if (!this.presenceDropWarned) {
        this.presenceDropWarned = true;
        logger.warn(
          `[Document] "${this.key}" was attached with disablePresence=true; ` +
            `presence updates from Document.update are silently dropped.`,
        );
      }
    }

    const rules = this.getSchemaRules();
    if (!ctx.isPresenceOnlyChange() && rules.length) {
      const result = validateYorkieRuleset(this.clone?.root.getObject(), rules);
      if (!result.valid) {
        this.clone = undefined;
        throw new YorkieError(
          Code.ErrDocumentSchemaValidationFailed,
          `schema validation failed: ${result.errors
            ?.map((error) => error.message)
            .join(', ')}`,
        );
      }
    }

    const size = totalDocSize(this.clone?.root.getDocSize());
    if (
      !ctx.isPresenceOnlyChange() &&
      this.maxSizeLimit > 0 &&
      this.maxSizeLimit < size
    ) {
      // NOTE(hackerwins): If the updater fails, we need to remove the cloneRoot and
      // clonePresences to prevent the user from accessing the invalid state.
      this.clone = undefined;
      throw new YorkieError(
        Code.ErrDocumentSizeExceedsLimit,
        `document size exceeded: ${size} > ${this.maxSizeLimit}`,
      );
    }

    // 02. Update the root object and presences from changes.
    if (ctx.hasChange()) {
      if (logger.isEnabled(LogLevel.Trivial)) {
        logger.trivial(`trying to update a local change: ${this.toJSON()}`);
      }

      const prev = {
        hadPresence: this.presences.has(actorID),
        wasOnline: this.status === DocStatus.Attached,
        presence: this.presences.has(actorID)
          ? deepcopy(this.presences.get(actorID)!)
          : undefined,
      };
      const change = ctx.toChange();
      let executed;
      try {
        executed = change.execute(this.root, this.presences, OpSource.Local);
      } catch (err) {
        // NOTE: `Change.execute` does not roll back, so an operation that
        // throws here leaves the clone holding the whole change while the
        // root holds only the prefix that applied, and the change is never
        // recorded. Drop the clone so the next access rebuilds it from the
        // root, the same way a failing updater above does.
        this.clone = undefined;

        // The prefix that landed burned this change's TimeTickets into the
        // root. `changeID` is not advanced here -- the change was never
        // queued -- so without this the next `update` builds its context from
        // the same ID, reissues those exact tickets and takes over the
        // prefix's slots in `elementPairMapByCreatedAt`, leaving two live
        // elements under one id. Burn the lamport (and this actor's version
        // vector entry with it) so no later ticket can collide, and restore
        // `clientSeq`: the counter names queued changes to the server, and
        // advancing it for a change nobody queued would leave a hole the
        // server rejects with `change clientSeq must increase by one`.
        if (!ctx.isPresenceOnlyChange()) {
          this.changeID = ctx
            .getNextID()
            .setClientSeq(this.changeID.getClientSeq());
        }

        throw err;
      }
      const { operations, opInfos, reverseOps } = executed;

      // NOTE(hackerwins): In update(Set), the element is replaced with a new value.
      // The history stack may still reference the old element's createdAt,
      // so we reconcile it to the new createdAt here.
      for (const op of change.getOperations()) {
        if (op instanceof ArraySetOperation) {
          this.internalHistory.reconcileCreatedAt(
            op.getCreatedAt(),
            op.getValue().getCreatedAt(),
          );
        }
      }

      // A plain local split can re-create an element a merge took away, too
      // (a peer merged two blocks and this user splits them again), and the
      // stacks may still name the merged-away element. Re-point them the way
      // `applyChangeInternal` does for a peer's split and `executeUndoRedo`
      // for an undo/redo one, before this change's own reverse is pushed.
      for (const op of operations) {
        if (op instanceof TreeEditOperation) {
          for (const [prevID, currID] of op.getSplitRecreatedIDs()) {
            this.internalHistory.reconcileTreeNodeID(
              op.getParentCreatedAt(),
              prevID,
              currID,
            );
          }
        }
      }

      const reversePresence = ctx.getReversePresence();
      if (reversePresence) {
        reverseOps.push({
          type: 'presence',
          value: reversePresence,
        });
      }

      this.localChanges.push(change);
      if (reverseOps.length) {
        this.internalHistory.pushUndo(reverseOps);
      }
      // NOTE(chacha912): Clear redo when a new local operation is applied.
      if (opInfos.length) {
        this.internalHistory.clearRedo();
      }
      this.changeID = ctx.getNextID();
      this.localChangeObserver.next();

      // 03. Publish the document change event.
      // Gated on the operations that RAN, not on the `OpInfo`s they produced,
      // matching `executeUndoRedoInternal`: an operation can mutate CRDT state
      // without yielding anything an editor could render (a style on a node a
      // peer removed concurrently has no index to report), and the change is
      // still queued into `localChanges` above and still consumes a
      // `clientSeq`. Preserve the change event even when its OpInfo is empty;
      // persistence observes the pending queue through a separate signal.
      const event: DocEvents<P> = [];
      if (operations.length) {
        event.push({
          type: DocEventType.LocalChange,
          source: OpSource.Local,
          value: {
            message: change.getMessage() || '',
            operations: opInfos,
            actor: actorID,
            clientSeq: change.getID().getClientSeq(),
            serverSeq: change.getID().getServerSeq(),
          },
          rawChange: this.isEnableDevtools() ? change.toStruct() : undefined,
        });
      }
      if (change.hasPresenceChange()) {
        const presenceEvent = this.reconcilePresence(
          actorID,
          prev,
          OpSource.Local,
        );
        if (presenceEvent) {
          event.push(presenceEvent);
        }
      }

      this.publish(event);

      if (logger.isEnabled(LogLevel.Trivial)) {
        logger.trivial(`after update a local change: ${this.toJSON()}`);
      }
    }
  }

  /**
   * `subscribe` registers a callback to subscribe to events on the document.
   * The callback will be called when the document is changed.
   */
  public subscribe(
    next: DocEventCallbackMap<P>['default'],
    error?: ErrorFn,
    complete?: CompleteFn,
  ): Unsubscribe;
  /**
   * `subscribe` registers a callback to subscribe to events on the document.
   * The callback will be called when the clients watching the document
   * establishe or update its presence.
   */
  public subscribe(
    type: 'presence',
    next: DocEventCallbackMap<P>['presence'],
    error?: ErrorFn,
    complete?: CompleteFn,
  ): Unsubscribe;
  /**
   * `subscribe` registers a callback to subscribe to events on the document.
   * The callback will be called when the current client establishes or updates its presence.
   */
  public subscribe(
    type: 'my-presence',
    next: DocEventCallbackMap<P>['my-presence'],
    error?: ErrorFn,
    complete?: CompleteFn,
  ): Unsubscribe;
  /**
   * `subscribe` registers a callback to subscribe to events on the document.
   * The callback will be called when the client establishes or terminates a connection,
   * or updates its presence.
   */
  public subscribe(
    type: 'others',
    next: DocEventCallbackMap<P>['others'],
    error?: ErrorFn,
    complete?: CompleteFn,
  ): Unsubscribe;
  /**
   * `subscribe` registers a callback to subscribe to events on the document.
   * The callback will be called when the stream connection status changes.
   */
  public subscribe(
    type: 'connection',
    next: DocEventCallbackMap<P>['connection'],
    error?: ErrorFn,
    complete?: CompleteFn,
  ): Unsubscribe;
  /**
   * `subscribe` registers a callback to subscribe to events on the document.
   * The callback will be called when the document status changes.
   */
  public subscribe(
    type: 'status',
    next: DocEventCallbackMap<P>['status'],
    error?: ErrorFn,
    complete?: CompleteFn,
  ): Unsubscribe;
  /**
   * `subscribe` registers a callback to subscribe to events on the document.
   * The callback will be called when the document is synced with the server.
   */
  public subscribe(
    type: 'sync',
    next: DocEventCallbackMap<P>['sync'],
    error?: ErrorFn,
    complete?: CompleteFn,
  ): Unsubscribe;
  /**
   * `subscribe` registers a callback to subscribe to events on the document.
   * The callback will be called when the targetPath or any of its nested values change.
   */
  public subscribe<TPath extends PathOf<R>, TOpInfo extends OpInfoOf<R, TPath>>(
    targetPath: TPath,
    next: NextFn<LocalChangeEvent<TOpInfo, P> | RemoteChangeEvent<TOpInfo, P>>,
    error?: ErrorFn,
    complete?: CompleteFn,
  ): Unsubscribe;
  /**
   * `subscribe` registers a callback to subscribe to events on the document.
   * The callback will be called when the authentification error occurs.
   */
  public subscribe(
    type: 'auth-error',
    next: DocEventCallbackMap<P>['auth-error'],
    error?: ErrorFn,
  ): Unsubscribe;
  /**
   * `subscribe` registers a callback to subscribe to events on the document.
   * The callback will be called when an epoch mismatch error occurs.
   */
  public subscribe(
    type: 'epoch-mismatch',
    next: DocEventCallbackMap<P>['epoch-mismatch'],
    error?: ErrorFn,
  ): Unsubscribe;
  /**
   * `subscribe` registers a callback to subscribe to events on the document.
   * The callback will be called when the offline-persistence layer had to
   * discard un-pushed local changes it could not reconcile (a data-loss event).
   */
  public subscribe(
    type: 'local-changes-dropped',
    next: DocEventCallbackMap<P>['local-changes-dropped'],
    error?: ErrorFn,
  ): Unsubscribe;
  /**
   * `subscribe` registers a callback to subscribe to events on the document.
   * The callback will be called when the offline-persistence layer stopped
   * persisting this document because writing it exceeds the configured budget.
   * Editing continues; durability does not.
   */
  public subscribe(
    type: 'persist-disabled',
    next: DocEventCallbackMap<P>['persist-disabled'],
    error?: ErrorFn,
  ): Unsubscribe;
  /**
   * `subscribe` registers a callback to subscribe to events on the document.
   */
  public subscribe(
    type: 'all',
    next: DocEventCallbackMap<P>['all'],
    error?: ErrorFn,
    complete?: CompleteFn,
  ): Unsubscribe;
  /**
   * `subscribe` registers a callback to subscribe to events on the document.
   */
  public subscribe<TPath extends PathOf<R>, TOpInfo extends OpInfoOf<R, TPath>>(
    arg1: TPath | DocEventTopic | DocEventCallbackMap<P>['default'],
    arg2?:
      | NextFn<LocalChangeEvent<TOpInfo, P> | RemoteChangeEvent<TOpInfo, P>>
      | DocEventCallback<P>
      | ErrorFn,
    arg3?: ErrorFn | CompleteFn,
    arg4?: CompleteFn,
  ): Unsubscribe {
    if (typeof arg1 === 'string') {
      if (typeof arg2 !== 'function') {
        throw new YorkieError(
          Code.ErrInvalidArgument,
          'Second argument must be a callback function',
        );
      }
      if (arg1 === 'presence') {
        const callback = arg2 as DocEventCallbackMap<P>['presence'];
        return this.eventStream.subscribe(
          (event) => {
            for (const docEvent of event) {
              if (
                docEvent.type !== DocEventType.Initialized &&
                docEvent.type !== DocEventType.Watched &&
                docEvent.type !== DocEventType.Unwatched &&
                docEvent.type !== DocEventType.PresenceChanged
              ) {
                continue;
              }

              callback(docEvent);
            }
          },
          arg3,
          arg4,
        );
      }
      if (arg1 === 'my-presence') {
        const callback = arg2 as DocEventCallbackMap<P>['my-presence'];
        return this.eventStream.subscribe(
          (event) => {
            for (const docEvent of event) {
              if (
                docEvent.type !== DocEventType.Initialized &&
                docEvent.type !== DocEventType.PresenceChanged
              ) {
                continue;
              }

              if (
                docEvent.type === DocEventType.PresenceChanged &&
                docEvent.value.clientID !== this.changeID.getActorID()
              ) {
                continue;
              }

              callback(docEvent);
            }
          },
          arg3,
          arg4,
        );
      }
      if (arg1 === 'others') {
        const callback = arg2 as DocEventCallbackMap<P>['others'];
        return this.eventStream.subscribe(
          (event) => {
            for (const docEvent of event) {
              if (
                docEvent.type !== DocEventType.Watched &&
                docEvent.type !== DocEventType.Unwatched &&
                docEvent.type !== DocEventType.PresenceChanged
              ) {
                continue;
              }

              if (docEvent.value.clientID !== this.changeID.getActorID()) {
                callback(docEvent);
              }
            }
          },
          arg3,
          arg4,
        );
      }
      if (arg1 === 'connection') {
        const callback = arg2 as DocEventCallbackMap<P>['connection'];
        return this.eventStream.subscribe(
          (event) => {
            for (const docEvent of event) {
              if (docEvent.type !== DocEventType.ConnectionChanged) {
                continue;
              }
              callback(docEvent);
            }
          },
          arg3,
          arg4,
        );
      }
      if (arg1 === 'status') {
        const callback = arg2 as DocEventCallbackMap<P>['status'];
        return this.eventStream.subscribe(
          (event) => {
            for (const docEvent of event) {
              if (docEvent.type !== DocEventType.StatusChanged) {
                continue;
              }
              callback(docEvent);
            }
          },
          arg3,
          arg4,
        );
      }
      if (arg1 === 'sync') {
        const callback = arg2 as DocEventCallbackMap<P>['sync'];
        return this.eventStream.subscribe(
          (event) => {
            for (const docEvent of event) {
              if (docEvent.type !== DocEventType.SyncStatusChanged) {
                continue;
              }
              callback(docEvent);
            }
          },
          arg3,
          arg4,
        );
      }
      if (arg1 === 'auth-error') {
        const callback = arg2 as DocEventCallbackMap<P>['auth-error'];
        return this.eventStream.subscribe((event) => {
          for (const docEvent of event) {
            if (docEvent.type !== DocEventType.AuthError) {
              continue;
            }

            callback(docEvent);
          }
        }, arg3);
      }
      if (arg1 === 'epoch-mismatch') {
        const callback = arg2 as DocEventCallbackMap<P>['epoch-mismatch'];
        return this.eventStream.subscribe((event) => {
          for (const docEvent of event) {
            if (docEvent.type !== DocEventType.EpochMismatch) {
              continue;
            }

            callback(docEvent);
          }
        }, arg3);
      }
      if (arg1 === 'persist-disabled') {
        const callback = arg2 as DocEventCallbackMap<P>['persist-disabled'];
        return this.eventStream.subscribe((event) => {
          for (const docEvent of event) {
            if (docEvent.type !== DocEventType.PersistDisabled) {
              continue;
            }

            callback(docEvent);
          }
        }, arg3);
      }
      if (arg1 === 'local-changes-dropped') {
        const callback =
          arg2 as DocEventCallbackMap<P>['local-changes-dropped'];
        return this.eventStream.subscribe((event) => {
          for (const docEvent of event) {
            if (docEvent.type !== DocEventType.LocalChangesDropped) {
              continue;
            }

            callback(docEvent);
          }
        }, arg3);
      }
      if (arg1 === 'all') {
        const callback = arg2 as DocEventCallbackMap<P>['all'];
        return this.eventStream.subscribe(callback, arg3, arg4);
      }
      const target = arg1;
      const callback = arg2 as NextFn<
        LocalChangeEvent<TOpInfo, P> | RemoteChangeEvent<TOpInfo, P>
      >;
      return this.eventStream.subscribe(
        (event) => {
          for (const docEvent of event) {
            if (
              docEvent.type !== DocEventType.LocalChange &&
              docEvent.type !== DocEventType.RemoteChange
            ) {
              continue;
            }

            const targetOps: Array<TOpInfo> = [];
            for (const op of docEvent.value.operations) {
              if (this.isSameElementOrChildOf(op.path, target)) {
                targetOps.push(op as TOpInfo);
              }
            }

            if (targetOps.length) {
              callback({
                ...docEvent,
                value: { ...docEvent.value, operations: targetOps },
              });
            }
          }
        },
        arg3,
        arg4,
      );
    }
    if (typeof arg1 === 'function') {
      const callback = arg1 as DocEventCallbackMap<P>['default'];
      const error = arg2 as ErrorFn;
      const complete = arg3 as CompleteFn;
      return this.eventStream.subscribe(
        (event) => {
          for (const docEvent of event) {
            if (
              docEvent.type !== DocEventType.Snapshot &&
              docEvent.type !== DocEventType.LocalChange &&
              docEvent.type !== DocEventType.RemoteChange
            ) {
              continue;
            }

            callback(docEvent);
          }
        },
        error,
        complete,
      );
    }

    throw new YorkieError(Code.ErrInvalidArgument, `"${arg1}" is not a valid`);
  }

  /**
   * `publish` triggers an event in this document, which can be received by
   * callback functions from document.subscribe().
   */
  public publish(events: DocEvents<P>) {
    if (this.eventStreamObserver) {
      this.eventStreamObserver.next(events);
    }
  }

  /**
   * `applyChangePack` applies the given change pack into this document.
   * 1. Remove local changes applied to server.
   * 2. Update the checkpoint.
   * 3. Do Garbage collection.
   */
  public applyChangePack(pack: ChangePack<P>): void {
    // 01. Apply snapshot or changes to the root object.
    //
    // NOTE: the checkpoint below is only reached once everything applied, so
    // a throw here leaves it where it was and the server redelivers this same
    // pack on every sync. Report that once, naming the checkpoint the
    // document is stuck at, before letting the error out: otherwise the only
    // field signal is a sync that never makes progress.
    //
    // This line runs at the default log level and repeats on every
    // redelivery, so everything it interpolates has to be metadata: the key,
    // the checkpoint, and an error that names the change and the operation by
    // type and ticket only. No operation payload — i.e. no document content —
    // may be carried into it.
    try {
      if (pack.hasSnapshot()) {
        this.applySnapshot(
          pack.getCheckpoint().getServerSeq(),
          pack.getVersionVector()!,
          pack.getSnapshot()!,
          pack.getCheckpoint().getClientSeq(),
        );
      } else {
        this.applyChanges(pack.getChanges(), OpSource.Remote);
        this.removePushedLocalChanges(pack.getCheckpoint().getClientSeq());
      }
    } catch (err) {
      logger.error(
        `[Document] "${this.key}" cannot apply the pack at checkpoint ` +
          `${this.checkpoint.toTestString()}; the server will redeliver it ` +
          `until this is resolved: ${err}`,
      );
      throw err;
    }

    // 02. Update the checkpoint.
    this.checkpoint = this.checkpoint.forward(pack.getCheckpoint());

    // 02-1. Learn the document's current compaction epoch from the server so a
    // subsequent attach/sync (and any persisted envelope) presents it back.
    this.epoch = pack.getEpoch();

    // 03. Do Garbage collection.
    if (!pack.hasSnapshot()) {
      this.garbageCollect(pack.getVersionVector()!);
    }

    // 04. Update the status.
    if (pack.getIsRemoved()) {
      this.applyStatus(DocStatus.Removed);
    }

    if (logger.isEnabled(LogLevel.Trivial)) {
      logger.trivial(`${this.root.toJSON()}`);
    }
  }

  /**
   * `getCheckpoint` returns the checkpoint of this document.
   */
  public getCheckpoint(): Checkpoint {
    return this.checkpoint;
  }

  /**
   * `getEpoch` returns the document's last-known compaction epoch.
   */
  public getEpoch(): bigint {
    return this.epoch;
  }

  /**
   * `getDocID` returns the server-assigned document id recorded on attach, or
   * an empty string before the first attach (or for a legacy envelope that
   * predates docID persistence).
   */
  public getDocID(): string {
    return this.docID;
  }

  /**
   * `setDocID` records the server-assigned document id so the next persisted
   * envelope carries it for the Tier-3 silent-purge guard.
   */
  public setDocID(docID: string): void {
    this.docID = docID;
  }

  /**
   * `getChangeID` returns the change id of this document.
   */
  public getChangeID(): ChangeID {
    return this.changeID;
  }

  /**
   * `hasLocalChanges` returns whether this document has local changes or not.
   */
  public hasLocalChanges(): boolean {
    return this.localChanges.length > 0;
  }

  /**
   * `toBytes` serializes the full restorable state of this document into a
   * self-contained byte envelope: the root and presences (as a snapshot), the
   * checkpoint, the changeID (lamport, version vector, actor), and the pending
   * local changes. `fromBytes` reverses it, so a document survives a reload
   * with its un-pushed edits intact. The envelope is a length-prefixed
   * concatenation of existing byte encodings rather than a new protobuf message.
   */
  public toBytes(): Uint8Array {
    const encoder = new TextEncoder();

    const snapshot = converter.snapshotToBytes(
      this.getRootObject(),
      this.presences,
    );
    const checkpoint = encoder.encode(
      JSON.stringify({
        serverSeq: this.checkpoint.getServerSeq().toString(),
        clientSeq: this.checkpoint.getClientSeq(),
      }),
    );
    const changeID = converter.changeIDToBinary(this.changeID);
    const pendingChanges = encoder.encode(
      JSON.stringify(this.localChanges.map((change) => change.toStruct())),
    );
    // The epoch is appended after the pending changes so an older envelope
    // (four blobs) still decodes: `fromBytes` treats a missing epoch blob as 0n.
    const epoch = encoder.encode(this.epoch.toString());
    // The docID is the last blob, appended after epoch, so an envelope written
    // before docID support (five blobs) still decodes: `fromBytes` treats a
    // missing docID blob as an empty string.
    const docID = encoder.encode(this.docID);

    return packBlobs([
      snapshot,
      checkpoint,
      changeID,
      pendingChanges,
      epoch,
      docID,
    ]);
  }

  /**
   * `fromBytes` reconstructs a document from the bytes produced by `toBytes`,
   * restoring the root, presences, checkpoint, changeID, and pending local
   * changes.
   */
  public static fromBytes<R, P extends Indexable = Indexable>(
    key: string,
    bytes: Uint8Array,
    opts?: DocumentOptions,
  ): Document<R, P> {
    const decoder = new TextDecoder();
    // Envelope invariant: trailing blobs are OPTIONAL for back-compat. A legacy
    // envelope written before a later field was added carries fewer blobs (e.g.
    // four blobs pre-epoch, five pre-docID), and each such blob is nil-guarded
    // below to a sensible default. This tolerate-fewer decode is intentional, so
    // no strict blob-count check is imposed. Any future field MUST be appended
    // as a new trailing blob and remain optional/nil-guarded here to keep older
    // envelopes decodable.
    const [
      snapshot,
      checkpointBytes,
      changeIDBytes,
      pendingChangesBytes,
      epochBytes,
      docIDBytes,
    ] = unpackBlobs(bytes);

    const doc = new Document<R, P>(key, opts);

    const { root, presences } = converter.bytesToSnapshot<P>(snapshot);
    doc.root = new CRDTRoot(root);
    doc.presences = presences;

    const checkpoint = JSON.parse(decoder.decode(checkpointBytes)) as {
      serverSeq: string;
      clientSeq: number;
    };
    doc.checkpoint = Checkpoint.of(
      BigInt(checkpoint.serverSeq),
      checkpoint.clientSeq,
    );

    doc.changeID = converter.bytesToChangeID(changeIDBytes);

    const structs = JSON.parse(decoder.decode(pendingChangesBytes)) as Array<
      ChangeStruct<P>
    >;
    doc.localChanges = structs.map((struct) => Change.fromStruct<P>(struct));

    // The epoch blob is optional: envelopes written before epoch support have
    // only four blobs, so treat an absent blob as the initial epoch 0n.
    doc.epoch = epochBytes ? BigInt(decoder.decode(epochBytes)) : 0n;

    // The docID blob is optional: envelopes written before docID support have
    // only five blobs, so treat an absent blob as an empty string.
    doc.docID = docIDBytes ? decoder.decode(docIDBytes) : '';

    return doc;
  }

  /**
   * `restoreFromBytes` rehydrates this document in place from the bytes
   * produced by `toBytes`, overwriting the root, presences, checkpoint,
   * changeID, and pending local changes. Unlike the static `fromBytes`, it
   * mutates the existing instance so a document the caller already holds (and
   * is about to attach) recovers its persisted, un-pushed state.
   *
   * Ordering: the caller must have stamped the actor (`setActor`) before
   * calling this. The rehydrated changes and changeID carry the actor they
   * were persisted under, and the snapshot's element actors are restored as
   * persisted, so this does not rely on `setActor` rewriting existing element
   * actors (a documented limitation of `setActor`).
   *
   * Actor guard: the persisted changeID must carry the same actor the caller
   * just stamped. If a store is reused under a different clientKey the current
   * stable actor differs from the persisted one; restoring anyway would stamp
   * subsequent edits with the current actor while the restored root/changes
   * keep the persisted actor, silently diverging the CRDT against a server that
   * keys on the current actor. On mismatch this throws `ErrClientNotActivated`-
   * free `ErrInvalidArgument` so the store-backed attach path can surface a
   * data-loss event and re-anchor instead of corrupting state.
   */
  public restoreFromBytes(bytes: Uint8Array): void {
    const currentActor = this.changeID.getActorID();
    const restored = Document.fromBytes<R, P>(this.key, bytes, this.opts);
    const restoredActor = restored.changeID.getActorID();
    if (currentActor && restoredActor && currentActor !== restoredActor) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        `persisted actor "${restoredActor}" does not match the current ` +
          `stable actor "${currentActor}"; the store was reused under a ` +
          `different client identity, restoring would diverge the CRDT`,
      );
    }
    this.root = restored.root;
    this.presences = restored.presences;
    this.checkpoint = restored.checkpoint;
    this.changeID = restored.changeID;
    this.localChanges = restored.localChanges;
    this.epoch = restored.epoch;
    this.docID = restored.docID;
    // Drop any stale clone so the next `update` re-clones from the restored
    // root/presences rather than the pre-restore state.
    this.clone = undefined;
    // Clear the undo/redo history: its reverse-ops reference the pre-restore
    // root/changeID that was just replaced, so a later undo/redo would apply
    // stale ticket references against the rehydrated state and corrupt it.
    this.clearHistory();
  }

  /**
   * `advanceClientSeqTo` moves this document's `clientSeq` counter forward to
   * the given sequence, and never backward.
   *
   * It exists for the one caller that has to undo a persisted header without
   * undoing the counter inside it. `restoreFromBytes` returns checkpoint,
   * epoch and `changeID` to what the snapshot carries, which is right for a
   * header the appended log cannot back — except for the counter. A counter is
   * not a claim about content the way a checkpoint is: it records which
   * `clientSeq` values this client has already MINTED, and the server has
   * taken some of them. Rewinding it to the snapshot's counter mints those
   * sequences a second time; the server skips them as duplicates and the next
   * ack, whose `clientSeq` covers them, drops them from `localChanges` as
   * pushed. The edits are lost with no event.
   *
   * The position to hand over is the acked checkpoint, not the header's
   * counter. The server validates continuity from the position it holds, so
   * the next change must be its `clientSeq` plus one; the counter can lead
   * that, and resuming there would mint past the server and wedge every later
   * push on `ErrInvalidClientSeq`.
   *
   * The counter only ever rises, so the guard is the whole contract: a caller
   * that hands over a stale position cannot pull the document back into
   * reusing sequence numbers.
   */
  public advanceClientSeqTo(clientSeq: number): void {
    if (clientSeq > this.changeID.getClientSeq()) {
      this.changeID = this.changeID.setClientSeq(clientSeq);
    }
  }

  /**
   * `getPendingChangeStructs` returns the serialized structs of the current
   * un-pushed local changes. Used by the offline-persistence layer to carry the
   * dropped changes in a `LocalChangesDropped` data-loss event so the app can
   * surface (and optionally re-apply) edits that could not be reconciled.
   */
  public getPendingChangeStructs(): Array<ChangeStruct<P>> {
    return this.localChanges.map((change) => change.toStruct());
  }

  /**
   * `subscribeLocalChangesInternal` observes newly queued local changes after
   * the document state and changeID have been committed. It runs before public
   * events so a subscriber-triggered sync cannot remove changes before the
   * persistence layer captures them. Public events can be empty or suppressed.
   *
   * @internal
   */
  public subscribeLocalChangesInternal(callback: () => void): Unsubscribe {
    return this.localChangeStream.subscribe(callback);
  }

  /**
   * `getPendingChangesAfter` returns the un-pushed local changes whose
   * `clientSeq` is above the given one, each paired with that sequence.
   *
   * The pairing is the point: a `ChangeStruct` carries its `clientSeq` encoded
   * inside the hex `changeID`, so a caller working from structs alone cannot
   * tell which changes it has already seen without decoding them. The
   * offline-persistence layer needs exactly that to append only what is new.
   */
  public getPendingChangesAfter(
    clientSeq: number,
  ): Array<{ clientSeq: number; struct: ChangeStruct<P> }> {
    const out: Array<{ clientSeq: number; struct: ChangeStruct<P> }> = [];
    for (const change of this.localChanges) {
      const seq = change.getID().getClientSeq();
      if (seq > clientSeq) {
        out.push({ clientSeq: seq, struct: change.toStruct() });
      }
    }
    return out;
  }

  /**
   * `metaToBytes` serializes just the checkpoint and changeID — the client's
   * position against the server — without touching the root.
   *
   * This is what the offline-persistence layer writes after a sync. A sync
   * advances the checkpoint while leaving the document unchanged, so
   * re-serializing the whole document to record it would cost time
   * proportional to the document for information that is a few dozen bytes.
   * Without it a restore would resume from whatever checkpoint the last
   * snapshot happened to carry.
   */
  public metaToBytes(): Uint8Array {
    const encoder = new TextEncoder();
    const checkpoint = encoder.encode(
      JSON.stringify({
        serverSeq: this.checkpoint.getServerSeq().toString(),
        clientSeq: this.checkpoint.getClientSeq(),
      }),
    );
    const changeID = converter.changeIDToBinary(this.changeID);
    // The epoch and docID are learned from sync responses, so meta is the only
    // place they can be recorded between snapshots. Omitting the epoch made a
    // server-side force-compaction invisible until the next attach presented a
    // stale one, took `ErrEpochMismatch`, and re-anchored — discarding every
    // un-pushed edit for want of a field.
    const encoderEpoch = encoder.encode(this.epoch.toString());
    const docID = encoder.encode(this.docID);
    return packBlobs([checkpoint, changeID, encoderEpoch, docID]);
  }

  /**
   * `restoreMetaFromBytes` applies the bytes produced by `metaToBytes`,
   * overwriting the checkpoint and changeID. Trailing blobs stay optional, the
   * same extension rule the `toBytes` envelope follows.
   */
  public restoreMetaFromBytes(bytes: Uint8Array): void {
    const decoder = new TextDecoder();
    const [checkpointBytes, changeIDBytes, epochBytes, docIDBytes] =
      unpackBlobs(bytes);

    const checkpoint = JSON.parse(decoder.decode(checkpointBytes)) as {
      serverSeq: string;
      clientSeq: number;
    };
    this.checkpoint = Checkpoint.of(
      BigInt(checkpoint.serverSeq),
      checkpoint.clientSeq,
    );
    if (changeIDBytes) {
      this.changeID = converter.bytesToChangeID(changeIDBytes);
    }
    // Trailing blobs stay optional, the same rule the `toBytes` envelope
    // follows, so meta written before these fields existed still decodes.
    if (epochBytes) {
      this.epoch = BigInt(decoder.decode(epochBytes));
    }
    if (docIDBytes) {
      this.docID = decoder.decode(docIDBytes);
    }
  }

  /**
   * `restoreAppendedChanges` replays changes that were recorded *after* the
   * snapshot this document was restored from, as the offline-persistence
   * layer's change log holds them.
   *
   * These are the opposite case to the pending changes carried inside a
   * `toBytes` envelope. Those are already reflected in the snapshot's root —
   * `toBytes` serializes the live root — so `fromBytes` queues them without
   * applying. A change from the log was written after that root was captured,
   * so it must be both **applied**, to bring the root forward, and **queued**,
   * so it is still pushed. Doing only the first loses the edit on reconnect;
   * doing only the second leaves the user looking at stale content.
   *
   * The log must be contiguous and ascending by `clientSeq`. A caller that
   * cannot satisfy that should restore from the snapshot alone and report the
   * loss rather than replaying a broken run.
   */
  public restoreAppendedChanges(
    structs: Array<ChangeStruct<P>>,
    ackedClientSeq = 0,
  ): void {
    if (!structs.length) {
      return;
    }

    const changes = structs.map((struct) => Change.fromStruct<P>(struct));
    let prev: number | undefined;
    for (const change of changes) {
      const clientSeq = change.getID().getClientSeq();
      if (prev !== undefined && clientSeq <= prev) {
        throw new YorkieError(
          Code.ErrInvalidArgument,
          `appended changes must be ascending by clientSeq, got ${clientSeq} ` +
            `after ${prev}`,
        );
      }
      prev = clientSeq;
    }

    // Every entry is applied — the log is the delta between the snapshot and
    // current content, so skipping an acked one would leave the root behind.
    // Only the unacked ones are queued: re-pushing what the server has already
    // taken presents a `clientSeq` it will skip.
    this.applyChanges(changes, OpSource.Local);
    this.localChanges.push(
      ...changes.filter(
        (change) => change.getID().getClientSeq() > ackedClientSeq,
      ),
    );

    // Adopt the last replayed change's ID as the document's own counter.
    //
    // `applyChanges` only syncs clocks, which leaves `clientSeq` behind and
    // over-advances `lamport` (it bumps per change, on top of a snapshot that
    // predates them). Both matter. `createChangePack` derives the pushed
    // checkpoint from `clientSeq`, so a counter left behind mints a sequence
    // the server has already seen and silently drops the next edit; and a
    // lamport that ran ahead mis-stamps every later ticket.
    //
    // An `update` leaves `changeID` equal to the change it just minted, so the
    // state after replaying a run is the last change's ID exactly.
    // Guarded: an all-acked replay must not pull the clock back below what the
    // meta header already established.
    const lastID = changes[changes.length - 1].getID();
    if (lastID.getClientSeq() >= this.changeID.getClientSeq()) {
      this.changeID = lastID;
    }

    // The clone predates the replay, and the history's reverse-ops reference
    // the pre-replay state — the same reasoning `restoreFromBytes` applies.
    this.clone = undefined;
    this.clearHistory();
    this.localChangeObserver.next();
  }

  /**
   * `resetForReanchor` drops all local state that was seeded from a stale
   * persisted envelope so the document can be re-attached fresh. The server
   * then re-anchors the client from the current snapshot. Used only on the
   * store-backed attach path when the resume is rejected with
   * `ErrEpochMismatch`: the persisted checkpoint/epoch/changeID and any
   * un-pushed local changes are stale relative to the compacted document, so
   * presenting them again would just be rejected. This mirrors constructing a
   * brand-new Document instance without forcing the caller to swap the object
   * reference it already holds.
   */
  public resetForReanchor(): void {
    this.changeID = InitialChangeID;
    this.checkpoint = InitialCheckpoint;
    this.localChanges = [];
    this.epoch = 0n;
    this.docID = '';
    this.root = CRDTRoot.create();
    this.presences = new Map();
    this.clone = undefined;
    // Clear the undo/redo history: its reverse-ops reference the stale
    // root/changeID that was just dropped, so a later undo/redo would apply
    // stale ticket references against the re-anchored state and corrupt it.
    this.clearHistory();
  }

  /**
   * `ensureClone` make a clone of root.
   */
  public ensureClone(): void {
    if (this.clone) {
      return;
    }

    this.clone = {
      root: this.root.deepcopy(),
      presences: deepcopy(this.presences),
    };
  }

  /**
   * `createChangePack` create change pack of the local changes to send to the
   * remote server.
   */
  public createChangePack(): ChangePack<P> {
    const changes = Array.from(this.localChanges);
    const checkpoint = this.checkpoint.increaseClientSeq(changes.length);
    return ChangePack.create(
      this.key,
      checkpoint,
      false,
      changes,
      this.getVersionVector(),
      undefined,
      this.epoch,
    );
  }

  /**
   * `setActor` sets actor into this document. This is also applied in the local
   * changes the document has.
   */
  public setActor(actorID: ActorID): void {
    for (const change of this.localChanges) {
      change.setActor(actorID);
    }
    this.changeID = this.changeID.setActor(actorID);

    // TODO(hackerwins): If the given actorID is not IntialActorID, we need to
    // update InitialActor of the root and clone.
  }

  /**
   * `setDisableGC` records whether this document participates in GC. The
   * client calls this on attach so subsequent applyChange runs use the
   * lamport-only sync path.
   */
  public setDisableGC(disableGC: boolean): void {
    this.disableGC = disableGC;
  }

  /**
   * `setDisablePresence` records the server-fixated presence-disabled state
   * of this document. The client calls this on attach (before
   * `applyChangePack`) so any subsequent `Document.update` invocation sees
   * the gating state already settled. Flipping the flag at runtime is
   * supported: the next `update` honours the new value.
   */
  public setDisablePresence(disablePresence: boolean): void {
    this.disablePresence = disablePresence;
  }

  /**
   * `isPresenceDisabled` returns the current presence-disabled state of
   * this document. Reflects the server-fixated value once attached;
   * before attach it reflects the local construction option.
   */
  public isPresenceDisabled(): boolean {
    return this.disablePresence;
  }

  /**
   * `isEnableDevtools` returns whether devtools is enabled or not.
   */
  public isEnableDevtools(): boolean {
    return !!this.opts.enableDevtools;
  }

  /**
   * `getKey` returns the key of this document.
   */
  public getKey(): string {
    return this.key;
  }

  /**
   * `getStatus` returns the status of this document.
   */
  public getStatus(): DocStatus {
    return this.status;
  }

  /**
   * `getClone` returns this clone.
   */
  public getClone() {
    return this.clone;
  }

  /**
   * `getCloneRoot` returns clone object.
   */
  public getCloneRoot(): CRDTObject | undefined {
    if (!this.clone) {
      return;
    }

    return this.clone.root.getObject();
  }

  /**
   * `getRoot` returns a new proxy of cloned root.
   */
  public getRoot(): JSONObject<R> {
    this.ensureClone();

    const ctx = ChangeContext.create(
      this.changeID.next(),
      this.clone!.root,
      this.clone!.presences.get(this.changeID.getActorID()) || ({} as P),
    );
    return createJSON<R>(ctx, this.clone!.root.getObject());
  }

  /**
   * `getDocSize` returns the size of this document.
   */
  public getDocSize(): DocSize {
    return this.root.getDocSize();
  }

  /**
   * `getMaxSizePerDocument` gets the maximum size of this document.
   */
  public getMaxSizePerDocument() {
    return this.maxSizeLimit;
  }

  /**
   * `setMaxSizePerDocument` sets the maximum size of this document.
   */
  public setMaxSizePerDocument(size: number) {
    this.maxSizeLimit = size;
  }

  /**
   * `getSchemaRules` gets the schema rules of this document.
   */
  public getSchemaRules() {
    return this.schemaRules;
  }

  /**
   * `setSchemaRules` sets the schema rules of this document.
   */
  public setSchemaRules(rules: Array<Rule>) {
    this.schemaRules = rules;
  }

  /**
   * `garbageCollect` purges elements that were removed before the given time.
   */
  public garbageCollect(minSyncedVersionVector: VersionVector): number {
    if (this.opts.disableGC) {
      return 0;
    }

    if (this.clone) {
      this.clone.root.garbageCollect(minSyncedVersionVector);
    }
    return this.root.garbageCollect(minSyncedVersionVector);
  }

  /**
   * `getRootObject` returns root object.
   */
  public getRootObject(): CRDTObject {
    return this.root.getObject();
  }

  /**
   * `getGarbageLen` returns the length of elements should be purged.
   */
  public getGarbageLen(): number {
    return this.root.getGarbageLen();
  }

  /**
   * `getRootCRDT` returns the CRDTRoot for testing purposes.
   * This method is intended for internal testing only.
   */
  public getRootCRDT(): CRDTRoot {
    return this.root;
  }

  /**
   * `getGarbageLenFromClone` returns the length of elements should be purged from clone.
   */
  public getGarbageLenFromClone(): number {
    return this.clone!.root.getGarbageLen();
  }

  /**
   * `toJSON` returns the JSON encoding of this document.
   */
  public toJSON(): string {
    return this.root.toJSON();
  }

  /**
   * `toSortedJSON` returns the sorted JSON encoding of this document.
   */
  public toSortedJSON(): string {
    return this.root.toSortedJSON();
  }

  /**
   * `getStats` returns the statistics of this document.
   */
  public getStats(): RootStats {
    return this.root.getStats();
  }

  /**
   * `toJSForTest` returns value with meta data for testing.
   */
  public toJSForTest(): Devtools.JSONElement {
    return {
      ...this.getRoot().toJSForTest(),
      key: 'root',
    };
  }

  /**
   * `applySnapshot` applies the given snapshot into this document.
   */
  public applySnapshot(
    serverSeq: bigint,
    snapshotVector: VersionVector,
    snapshot?: Uint8Array,
    clientSeq: number = -1,
  ) {
    const { root, presences } = converter.bytesToSnapshot<P>(snapshot);
    this.root = new CRDTRoot(root);
    this.presences = presences;
    this.changeID = this.changeID.setClocks(
      snapshotVector.maxLamport(),
      snapshotVector,
    );

    // drop clone because it is contaminated.
    this.clone = undefined;

    this.removePushedLocalChanges(clientSeq);

    // NOTE(hackerwins): If the document has local changes, we need to apply
    // them after applying the snapshot, as local changes are not included in the snapshot data.
    // Afterward, we should publish a snapshot event with the latest
    // version of the document to ensure the user receives the most up-to-date snapshot.
    this.applyChanges(this.localChanges, OpSource.Local);
    this.clearHistory();
    this.publish([
      {
        type: DocEventType.Snapshot,
        source: OpSource.Remote,
        value: {
          serverSeq: serverSeq.toString(),
          snapshot: this.isEnableDevtools()
            ? converter.bytesToHex(snapshot)
            : undefined,
          snapshotVector: converter.versionVectorToHex(snapshotVector),
        },
      },
    ]);
  }

  /**
   * `clearHistory` flushes both undo and redo stacks. This is used
   * after applying a snapshot or initialRoot so that setup operations
   * are not reachable via undo.
   */
  public clearHistory() {
    this.internalHistory.clearRedo();
    this.internalHistory.clearUndo();
  }

  /**
   * `applyChanges` applies the given changes into this document.
   */
  public applyChanges(changes: Array<Change<P>>, source: OpSource): void {
    if (logger.isEnabled(LogLevel.Debug)) {
      logger.debug(
        `trying to apply ${changes.length} remote changes.` +
          `elements:${this.root.getElementMapSize()}, ` +
          `removeds:${this.root.getGarbageElementSetSize()}`,
      );
    }
    if (logger.isEnabled(LogLevel.Trivial)) {
      logger.trivial(
        changes
          .map(
            (change) =>
              `${change.getID().toTestString()}\t${change.toTestString()}`,
          )
          .join('\n'),
      );
    }

    for (const change of changes) {
      this.applyChange(change, source);
    }

    if (logger.isEnabled(LogLevel.Debug)) {
      logger.debug(
        `after appling ${changes.length} remote changes.` +
          `elements:${this.root.getElementMapSize()}, ` +
          ` removeds:${this.root.getGarbageElementSetSize()}`,
      );
    }
  }

  /**
   * `applyChange` applies the given change into this document.
   */
  public applyChange(change: Change<P>, source: OpSource) {
    try {
      this.applyChangeInternal(change, source);
    } catch (err) {
      // NOTE: `Change.execute` does not roll back, so a change that fails
      // partway leaves the clone and the root holding different prefixes of
      // it. Drop the clone so the next access rebuilds it from the root, the
      // way `update` does on failure.
      this.clone = undefined;

      // NOTE: only a remote change is named. The checkpoint only advances
      // after the changes of a pack have been applied, so a remote change
      // that throws is redelivered by the server forever and needs to be
      // diagnosable rather than looking like a network problem. A local
      // replay (`restoreAppendedChanges`) or a devtools-driven local apply
      // has a caller waiting on it, and rewriting the error it threw would
      // hide the code that caller matches on.
      if (source !== OpSource.Remote) {
        throw err;
      }

      throw err instanceof ChangeApplyError
        ? err.withDocKey(this.key)
        : new ChangeApplyError({
            docKey: this.key,
            changeID: change.getID().toTestString(),
            cause: err,
          });
    }
  }

  /**
   * `applyChangeInternal` applies the given change into the clone and the root.
   */
  private applyChangeInternal(change: Change<P>, source: OpSource) {
    this.ensureClone();
    change.execute(this.clone!.root, this.clone!.presences, source);

    const events: DocEvents<P> = [];
    const actorID = change.getID().getActorID();

    // Capture prev state before execute updates this.presences
    const prev = change.hasPresenceChange()
      ? {
          hadPresence: this.presences.has(actorID),
          wasOnline: this.onlineClients.has(actorID),
          presence: this.presences.has(actorID)
            ? deepcopy(this.presences.get(actorID)!)
            : undefined,
        }
      : undefined;

    const { opInfos, operations } = change.execute(
      this.root,
      this.presences,
      source,
    );
    for (const op of operations) {
      if (op instanceof EditOperation) {
        const [from, to] = op.normalizePos(this.root);

        this.internalHistory.reconcileTextEdit(
          op.getParentCreatedAt(),
          from,
          to,
          op.getContent()?.length ?? 0,
        );
      }
      if (op instanceof TreeEditOperation) {
        // A split re-creates the elements a merge took away, under brand-new
        // ids -- see `CRDTTree.mergeSourceOf`. That happens whoever sent the
        // split: `executeUndoRedo` re-points this replica's stacks when the
        // split is its own, and here when it arrives from a peer. Left
        // un-re-pointed, an entry still naming the element the peer replaced
        // addresses a node garbage collection will purge, and the change it
        // eventually pushes is rejected on every replica (#1425).
        //
        // Done before the index reconciliation below and before the event is
        // published, for the same reason the undo/redo path defers to after
        // execution: the pairs are only known once the split has run.
        for (const [prev, curr] of op.getSplitRecreatedIDs()) {
          this.internalHistory.reconcileTreeNodeID(
            op.getParentCreatedAt(),
            prev,
            curr,
          );
        }

        // One reconciliation per range the op actually changed, in the order
        // it changed them: an identity-preserving restore/retombstone revives
        // or re-removes several nodes at positions its stored indices never
        // describe, and each measurement is relative to the one before it.
        for (const [from, to, contentSize] of op.getExecutedRanges()) {
          this.internalHistory.reconcileTreeEdit(
            op.getParentCreatedAt(),
            from,
            to,
            contentSize,
          );
        }
      }
    }
    this.changeID = this.disableGC
      ? this.changeID.syncLamport(change.getID())
      : this.changeID.syncClocks(change.getID());
    // Gated on the operations that RAN, not on the `OpInfo`s they produced,
    // for the same reason as the undo/redo path in `executeUndoRedo`: an
    // operation can change CRDT state without producing anything an editor
    // could render (a style on a node the sender removed concurrently, a Tree
    // restore whose revived nodes all sit under a removed ancestor). Such a
    // change still moved this replica and still occupies a `serverSeq`, so a
    // peer applying it has to see the event -- otherwise devtools replay and
    // any subscriber counting changes lose it.
    if (operations.length) {
      const rawChange = this.isEnableDevtools() ? change.toStruct() : undefined;
      events.push(
        source === OpSource.Remote
          ? {
              type: DocEventType.RemoteChange,
              source,
              value: {
                actor: actorID,
                clientSeq: change.getID().getClientSeq(),
                serverSeq: change.getID().getServerSeq(),
                message: change.getMessage() || '',
                operations: opInfos,
              },
              rawChange,
            }
          : {
              type: DocEventType.LocalChange,
              source,
              value: {
                actor: actorID,
                clientSeq: change.getID().getClientSeq(),
                serverSeq: change.getID().getServerSeq(),
                message: change.getMessage() || '',
                operations: opInfos,
              },
              rawChange,
            },
      );
    }
    if (prev && change.hasPresenceChange()) {
      const presenceChange = change.getPresenceChange()!;
      if (presenceChange.type === PresenceChangeType.Clear) {
        this.removeOnlineClient(actorID);
      }
      const presenceEvent = this.reconcilePresence(actorID, prev, source);
      if (presenceEvent) {
        events.push(presenceEvent);
      }
    }
    // DocEvent should be emitted synchronously with applying changes.
    // This is because 3rd party model should be synced with the Document
    // after RemoteChange event is emitted. If the event is emitted
    // asynchronously, the model can be changed and breaking consistency.
    if (events.length) {
      this.publish(events);
    }
  }

  /**
   * `applyWatchInit` applies the watch initialization with the given client IDs.
   */
  public applyWatchInit(clientIDs: Array<string>) {
    const onlineClients: Set<ActorID> = new Set();
    for (const clientID of clientIDs) {
      if (clientID === this.changeID.getActorID()) {
        continue;
      }

      onlineClients.add(clientID);
    }
    this.setOnlineClients(onlineClients);

    // NOTE(hackerwins): We intentionally do NOT prune presences for clients
    // no longer in the online set. The presences map retains stale entries,
    // but getPresences() / getOthersPresences() already filter by
    // onlineClients, so stale data is never exposed to the caller.
    //
    // Keeping stale presences allows correct recovery when a client's watch
    // stream reconnects: the server sends DOCUMENT_WATCHED, applyDocEvent
    // checks hasPresence(publisher), and because the old presence is still
    // in the map the "watched" event fires and the client becomes visible
    // again. Previously, pruning here deleted the presence, causing
    // hasPresence to return false and silently dropping the reconnected peer.

    this.publish([
      {
        type: DocEventType.Initialized,
        source: OpSource.Local,
        value: this.getPresences(),
      },
    ]);
  }

  /**
   * `applyDocEvent` applies the given doc event into this document.
   */
  public applyDocEvent(type: PbDocEventType, publisher: string) {
    const prev = {
      hadPresence: this.presences.has(publisher),
      wasOnline: this.onlineClients.has(publisher),
      presence: this.presences.has(publisher)
        ? deepcopy(this.presences.get(publisher)!)
        : undefined,
    };

    if (type === PbDocEventType.DOCUMENT_WATCHED) {
      if (this.onlineClients.has(publisher) && this.hasPresence(publisher)) {
        return;
      }
      this.addOnlineClient(publisher);
    } else if (type === PbDocEventType.DOCUMENT_UNWATCHED) {
      this.removeOnlineClient(publisher);
      this.presences.delete(publisher);
    }

    const event = this.reconcilePresence(publisher, prev, OpSource.Remote);
    if (event) {
      this.publish([event]);
    }
  }

  /**
   * `applyStatus` applies the document status into this document.
   */
  public applyStatus(status: DocStatus) {
    const actorID = this.changeID.getActorID();
    const prev = {
      hadPresence: this.presences.has(actorID),
      wasOnline: this.status === DocStatus.Attached,
      presence: this.presences.has(actorID)
        ? deepcopy(this.presences.get(actorID)!)
        : undefined,
    };

    this.status = status;

    if (status === DocStatus.Detached) {
      this.setActor(InitialActorID);
    }

    const event = this.reconcilePresence(actorID, prev, OpSource.Local);
    if (event) {
      this.publish([event]);
    }

    this.publish([
      {
        source: status === DocStatus.Removed ? OpSource.Remote : OpSource.Local,
        type: DocEventType.StatusChanged,
        value:
          status === DocStatus.Attached
            ? { status, actorID: this.changeID.getActorID() }
            : { status },
      },
    ]);
  }

  /**
   * `applyDocEventsForReplay` applies the given events into this document.
   */
  public applyDocEventsForReplay(events: Array<Devtools.DocEventForReplay<P>>) {
    for (const event of events) {
      if (event.type === DocEventType.StatusChanged) {
        this.applyStatus(event.value.status);
        if (event.value.status === DocStatus.Attached) {
          this.setActor(event.value.actorID);
        }
        continue;
      }

      if (event.type === DocEventType.Snapshot) {
        const { snapshot, serverSeq, snapshotVector } = event.value;
        if (!snapshot) continue;

        // TODO(hackerwins): We need to check version vector and lamport clock
        // of the snapshot is valid.
        this.applySnapshot(
          BigInt(serverSeq),
          converter.hexToVersionVector(snapshotVector),
          converter.hexToBytes(snapshot),
        );
        continue;
      }

      if (
        event.type === DocEventType.LocalChange ||
        event.type === DocEventType.RemoteChange
      ) {
        if (!event.rawChange) continue;
        const change = Change.fromStruct<P>(event.rawChange);
        this.applyChange(change, event.source);
      }

      if (event.type === DocEventType.Initialized) {
        const onlineClients: Set<ActorID> = new Set();
        for (const { clientID, presence } of event.value) {
          onlineClients.add(clientID);
          this.presences.set(clientID, presence);
        }
        this.setOnlineClients(onlineClients);
      } else if (event.type === DocEventType.Watched) {
        const { clientID, presence } = event.value;
        this.addOnlineClient(clientID);
        this.presences.set(clientID, presence);
      } else if (event.type === DocEventType.Unwatched) {
        const { clientID } = event.value;
        this.removeOnlineClient(clientID);
        this.presences.delete(clientID);
      } else if (event.type === DocEventType.PresenceChanged) {
        const { clientID, presence } = event.value;
        this.presences.set(clientID, presence);
      }
    }
  }

  /**
   * `getValueByPath` returns the JSONElement corresponding to the given path.
   */
  public getValueByPath(path: string): JSONElement | undefined {
    if (!path.startsWith('$')) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        `path must start with "$"`,
      );
    }
    const paths = path.split('.');
    paths.shift();
    let value: JSONObject<any> = this.getRoot();
    for (const key of paths) {
      value = value[key];
      if (!value) return undefined;
    }
    return value;
  }

  /**
   * `setOnlineClients` sets the given online client set.
   */
  public setOnlineClients(onlineClients: Set<ActorID>) {
    this.onlineClients = onlineClients;
  }

  /**
   * `resetOnlineClients` resets the online client set.
   */
  public resetOnlineClients() {
    this.onlineClients = new Set();
  }

  /**
   * `addOnlineClient` adds the given clientID into the online client set.
   */
  public addOnlineClient(clientID: ActorID) {
    this.onlineClients.add(clientID);
  }

  /**
   * `removeOnlineClient` removes the clientID from the online client set.
   */
  public removeOnlineClient(clientID: ActorID) {
    this.onlineClients.delete(clientID);
  }

  /**
   * `reconcilePresence` compares the previous and current state of a client's
   * presence/online status and returns the appropriate event to emit.
   *
   * For remote clients, "online" means the client is in onlineClients.
   * For self, "online" means the document status is Attached.
   *
   * State transition table:
   *   (!hadP || !wasOn) → (hasP && isOn)  : watched (remote) or presence-changed (self)
   *   (hadP && wasOn)   → (hasP && isOn)  : presence-changed
   *   (hadP && wasOn)   → (!hasP || !isOn): unwatched (remote only)
   *   otherwise                           : no event (waiting)
   */
  private reconcilePresence(
    actorID: ActorID,
    prev: { hadPresence: boolean; wasOnline: boolean; presence?: P },
    source: OpSource,
  ): WatchedEvent<P> | UnwatchedEvent<P> | PresenceChangedEvent<P> | undefined {
    const isSelf = actorID === this.changeID.getActorID();
    const hasPresence = this.presences.has(actorID);
    const isOnline = isSelf
      ? this.status === DocStatus.Attached
      : this.onlineClients.has(actorID);

    if (!hasPresence || !isOnline) {
      // Transitioned from ready → not ready: unwatched (remote only)
      if (prev.hadPresence && prev.wasOnline && !isSelf) {
        return {
          type: DocEventType.Unwatched,
          source: OpSource.Remote,
          value: {
            clientID: actorID,
            presence: prev.presence!,
          },
        };
      }
      return undefined;
    }

    const presence = deepcopy(this.presences.get(actorID)!);

    if (!prev.hadPresence || !prev.wasOnline) {
      // Transitioned from not-ready → ready
      if (isSelf) {
        return {
          type: DocEventType.PresenceChanged,
          source,
          value: { clientID: actorID, presence },
        };
      }
      return {
        type: DocEventType.Watched,
        source: OpSource.Remote,
        value: { clientID: actorID, presence },
      };
    }

    // Both were ready and still are: presence value changed
    return {
      type: DocEventType.PresenceChanged,
      source,
      value: { clientID: actorID, presence },
    };
  }

  /**
   * `hasPresence` returns whether the given clientID has a presence or not.
   */
  public hasPresence(clientID: ActorID): boolean {
    return this.presences.has(clientID);
  }

  /**
   * `getMyPresence` returns the presence of the current client.
   */
  public getMyPresence(): P {
    // TODO(chacha912): After resolving the presence initialization issue,
    // remove default presence.(#608)
    if (this.status !== DocStatus.Attached) {
      return {} as P;
    }

    const p = this.presences.get(this.changeID.getActorID())!;
    return p ? deepcopy(p) : ({} as P);
  }

  /**
   * `getOthersPresences` returns the presences of all other clients.
   */
  public getOthersPresences(): Array<{ clientID: ActorID; presence: P }> {
    const others: Array<{ clientID: ActorID; presence: P }> = [];
    const myClientID = this.changeID.getActorID();

    for (const clientID of this.onlineClients) {
      if (clientID !== myClientID && this.presences.has(clientID)) {
        others.push({
          clientID,
          presence: deepcopy(this.presences.get(clientID)!),
        });
      }
    }

    return others;
  }

  /**
   * `getPresence` returns the presence of the given clientID.
   */
  public getPresence(clientID: ActorID): P | undefined {
    if (clientID === this.changeID.getActorID()) {
      return this.getMyPresence();
    }

    if (!this.onlineClients.has(clientID)) return;
    const p = this.presences.get(clientID);
    return p ? deepcopy(p) : undefined;
  }

  /**
   * `getPresences` returns the presences of online clients.
   */
  public getPresences(): Array<{ clientID: ActorID; presence: P }> {
    const presences: Array<{ clientID: ActorID; presence: P }> = [];
    presences.push({
      clientID: this.changeID.getActorID(),
      presence: deepcopy(this.getMyPresence()),
    });

    for (const clientID of this.onlineClients) {
      if (this.presences.has(clientID)) {
        presences.push({
          clientID,
          presence: deepcopy(this.presences.get(clientID)!),
        });
      }
    }
    return presences;
  }

  /**
   * `getPresenceForTest` returns the presence of the given clientID
   * regardless of whether the client is online or not.
   */
  public getPresenceForTest(clientID: ActorID): P | undefined {
    const p = this.presences.get(clientID);
    return p ? deepcopy(p) : undefined;
  }

  /**
   * `getSelfForTest` returns the client that has attached this document.
   */
  public getSelfForTest() {
    return {
      clientID: this.getChangeID().getActorID(),
      presence: this.getMyPresence(),
    };
  }

  /**
   * `getOthersForTest` returns all the other clients in online, sorted by clientID.
   */
  public getOthersForTest() {
    const myClientID = this.getChangeID().getActorID();

    return this.getPresences()
      .filter((a) => a.clientID !== myClientID)
      .sort((a, b) => (a.clientID > b.clientID ? 1 : -1));
  }

  /**
   * `getUndoStackForTest` returns the undo stack for test.
   */
  public getUndoStackForTest(): Array<Array<HistoryOperation<P>>> {
    return this.internalHistory.getUndoStackForTest();
  }

  /**
   * `getRedoStackForTest` returns the redo stack for test.
   */
  public getRedoStackForTest(): Array<Array<HistoryOperation<P>>> {
    return this.internalHistory.getRedoStackForTest();
  }

  /**
   * `getVersionVector` returns the version vector of document
   */
  public getVersionVector() {
    return this.changeID.getVersionVector();
  }

  private isSameElementOrChildOf(elem: string, parent: string): boolean {
    if (parent === elem) {
      return true;
    }

    const nodePath = elem.split('.');
    return parent.split('.').every((path, index) => path === nodePath[index]);
  }

  /**
   * `acknowledgePushedChanges` removes the local changes the server has
   * confirmed, and forwards only the client seq of the checkpoint. It is for a
   * response pack dropped without applying its remote state: the server seq
   * must stay put so the skipped state is pulled again later, while the
   * confirmed changes must not be pushed again. The server dedupes a re-pushed
   * change when storing it, but a snapshot it builds for the same request
   * would apply that change a second time.
   *
   * The pack's *metadata* is not remote state, and is taken in full: the
   * compaction epoch and the removal flag describe the document itself, not
   * the content being skipped, and neither is re-sent by a later pull the way
   * the skipped changes are.
   *
   * @internal
   */
  public acknowledgePushedChanges(pack: ChangePack<P>): void {
    const clientSeq = pack.getCheckpoint().getClientSeq();
    this.removePushedLocalChanges(clientSeq);
    this.checkpoint = this.checkpoint.forward(
      Checkpoint.of(this.checkpoint.getServerSeq(), clientSeq),
    );

    // Dropping the epoch would leave the client presenting a superseded one on
    // the next request, which the server answers with `ErrEpochMismatch` — a
    // re-anchor that discards exactly the un-pushed edits PushOnly exists to
    // keep. A compaction is also the shape that arrives as a snapshot, i.e.
    // precisely the pack this path drops.
    this.epoch = pack.getEpoch();

    // A removal is terminal: there is no later pull to learn it from, because
    // the server row is gone. Skipping it leaves the document attached and its
    // persisted envelope pointing at a row that no longer exists.
    if (pack.getIsRemoved()) {
      this.applyStatus(DocStatus.Removed);
    }
  }

  /**
   * `removePushedLocalChanges` removes local changes that have been applied to
   * the server from the local changes.
   *
   * @param clientSeq - client sequence number to remove local changes before it
   */
  private removePushedLocalChanges(clientSeq: number) {
    while (this.localChanges.length) {
      const change = this.localChanges[0];
      if (change.getID().getClientSeq() > clientSeq) {
        break;
      }
      this.localChanges.shift();
    }
  }

  /**
   * `executeUndoRedo` executes undo or redo operation with shared logic.
   */
  private executeUndoRedo(isUndo: boolean): void {
    if (this.isUpdating) {
      throw new YorkieError(
        Code.ErrRefused,
        `${isUndo ? 'Undo' : 'Redo'} is not allowed during an update`,
      );
    }

    // NOTE: The refusal above must not drop the clone: an updater is holding
    // a proxy over it, and `update` reads it again after the updater returns.
    try {
      this.executeUndoRedoInternal(isUndo);
    } catch (err) {
      // A partially executed undo/redo leaves the clone ahead of the root;
      // drop it so the next access rebuilds it from the root.
      this.clone = undefined;
      throw err;
    }
  }

  /**
   * `executeUndoRedoInternal` pops the history entry and applies it to the
   * clone and the root.
   */
  private executeUndoRedoInternal(isUndo: boolean): void {
    const ops = isUndo
      ? this.internalHistory.popUndo()
      : this.internalHistory.popRedo();

    if (!ops) {
      return;
    }

    this.ensureClone();
    // TODO(chacha912): After resolving the presence initialization issue,
    // remove default presence.(#608)
    const ctx = ChangeContext.create<P>(
      this.changeID,
      this.clone!.root,
      this.clone!.presences.get(this.changeID.getActorID()) || ({} as P),
    );

    // Tree node ids this entry re-mints, and what they replace. Collected
    // while the change is built and applied to the history stacks only once
    // the operations have run — see the split branch below.
    const pending: Array<{
      op: TreeEditOperation;
      prev: CRDTTreeNodeID;
      curr: CRDTTreeNodeID;
      // Which of the operation's split tickets `curr` is, so the drain can
      // tell whether the ROOT execution got that far — the hook that records
      // the pair fires on the clone pass first, and the two passes need not
      // mint the same number of elements. -1 for a re-issued content id,
      // which is minted unconditionally.
      ticketIndex: number;
    }> = [];
    // The splitting operations whose re-pointing hook is still registered,
    // so it can be taken off once the change has been applied — it closes
    // over this entry, which outlives nothing here.
    const splitOps: Array<TreeEditOperation> = [];
    /**
     * `repointRest` re-points the operations that follow `op` in this entry
     * from `prev` to `curr`.
     */
    const repointRest = (
      op: Operation,
      prev: CRDTTreeNodeID,
      curr: CRDTTreeNodeID,
    ) => {
      for (const later of ops.slice(ops.indexOf(op) + 1)) {
        if (
          later instanceof TreeEditOperation ||
          later instanceof TreeStyleOperation
        ) {
          later.reconcileNodeID(prev, curr);
        }
      }
    };

    // apply undo/redo operation in the context to generate a change
    for (const op of ops) {
      if (!(op instanceof Operation)) {
        // apply presence change to the context
        const presence = new Channel<P>(
          ctx,
          deepcopy(this.clone!.presences.get(this.changeID.getActorID())!),
        );
        presence.set(op.value, { addToHistory: true });
        continue;
      }

      const ticket = ctx.issueTimeTicket();
      op.setExecutedAt(ticket);

      // A Set/Add/ArraySet reverse carries a deepcopy of the value it
      // restores, and that copy keeps the split-sibling links of the tree it
      // was taken from. Every other replica decodes this same operation
      // through `dropSplitLinksInElement`, so without this the replica that
      // ran the undo is the only one left holding the links, and the two
      // disagree from the next same-boundary split on.
      if (
        op instanceof SetOperation ||
        op instanceof AddOperation ||
        op instanceof ArraySetOperation
      ) {
        dropSplitLinksInElement(op.getValue());
      }

      // NOTE(hackerwins): In undo/redo, both Set and Add may act as updates.
      // - Set: replaces the element value.
      // - Add: serves as UndoRemove, effectively restoring a deleted element.
      // In both cases, the history stack may point to the old createdAt,
      // so we update it to the new createdAt.
      if (op instanceof ArraySetOperation) {
        const prev = op.getCreatedAt();
        op.getValue().setCreatedAt(ticket);
        this.internalHistory.reconcileCreatedAt(prev, ticket);
      } else if (op instanceof AddOperation) {
        const prev = op.getValue().getCreatedAt();
        op.getValue().setCreatedAt(ticket);
        this.internalHistory.reconcileCreatedAt(prev, ticket);
      } else if (op instanceof TreeEditOperation) {
        // A reverse that re-inserts a copy of removed nodes carries their
        // original ids; inserting them again would leave two nodes under one
        // id. Restore-mode reverses revive by identity and keep theirs.
        //
        // Same hazard as the split below: the nodes come back under brand-new
        // ids, so anything else recorded against the old ones has to follow.
        for (const [prev, curr] of op.reissueContentIDs(() =>
          ctx.issueTimeTicket(),
        )) {
          repointRest(op, prev, curr);
          pending.push({ op, prev, curr, ticketIndex: -1 });
        }

        // A split reverse -- the undo of a merge, or the redo of a split --
        // mints one element per split level, and each needs a ticket the
        // loop above did not issue: it issues exactly one per operation.
        // Left without them, `TreeEditOperation.execute` falls back to
        // reconstructing them by counting delimiters up from its own
        // executedAt, which runs straight over the ticket the NEXT operation
        // in this same entry was issued -- landing two LIVE elements under
        // one id, on every replica and on the server, since the change
        // carries both operations. Recording them on the operation is what
        // stops any other replica reconstructing them either.
        //
        // One per level is an upper bound, not an exact count: the split
        // stops early when it reaches the root. A ticket nobody consumes
        // only advances the delimiter, while one short would silently
        // reopen the fallback. Mirrors yorkie's `executeUndoRedo`.
        const level = op.getSplitLevel();
        if (level > 0) {
          const tickets = Array.from({ length: level }, () =>
            ctx.issueTimeTicket(),
          );
          op.setSplitTickets(tickets);

          // The split re-creates elements a merge removed, under the new
          // tickets. Operations recorded against the old elements -- the
          // rest of this entry (the style that followed a split, say) and
          // any other entry in the stacks -- still name them. Once garbage
          // collection has purged the old elements, a replica applying such
          // an operation cannot find them and rejects the whole change
          // (#1425). Re-point them, so the change is encoded with the ids
          // every replica will have.
          //
          // Both re-pointings are driven from the split itself, as it takes
          // each ticket: only then is it known that the element exists. The
          // split stops as soon as it runs out of ancestors, so a level it
          // never reached mints nothing, and re-pointing that level up front
          // -- on the strength of `splitLevel` alone -- would leave the
          // operation naming a ticket no node ever received, which
          // `toTreeNodePair` cannot resolve. The rest of THIS entry cannot
          // wait for the split to return either: those operations run in the
          // same change, right after it, so the hook fires mid-execution,
          // while they are still pending. The history stacks could wait, but
          // are collected here and applied below for a different reason --
          // the new reverse ops are built from the executed state and must
          // not be pushed before the stacks are re-pointed.
          op.onSplitTicketConsumed((index) => {
            // Read through the operation rather than off `tickets` and a
            // captured array: an earlier re-point in this same entry may have
            // replaced `replacedIDs` wholesale (`reconcileNodeID`).
            const prev = op.getReplacedIDs()[index];
            if (!prev) {
              return;
            }
            const curr = CRDTTreeNodeID.of(op.getSplitTickets()[index], 0);
            // Called once per execution, and the change is executed twice
            // (clone, then root). `repointRest` no longer finds `prev` the
            // second time, but the pending entry would be a duplicate.
            if (pending.some((p) => p.op === op && p.prev.equals(prev))) {
              return;
            }
            repointRest(op, prev, curr);
            pending.push({ op, prev, curr, ticketIndex: index });
          });
          splitOps.push(op);
        }
      }

      ctx.push(op);
    }

    const change = ctx.toChange();
    const actorID = this.changeID.getActorID();
    const prev = {
      hadPresence: this.presences.has(actorID),
      wasOnline: this.status === DocStatus.Attached,
      presence: this.presences.has(actorID)
        ? deepcopy(this.presences.get(actorID)!)
        : undefined,
    };
    let executed;
    try {
      change.execute(
        this.clone!.root,
        this.clone!.presences,
        OpSource.UndoRedo,
      );
      try {
        executed = change.execute(this.root, this.presences, OpSource.UndoRedo);
      } catch (err) {
        // Same hazard as the root pass in `update()`: the operations that ran
        // burned their tickets into the root, the change is never queued and
        // `changeID` never advances, so the next change would reissue them.
        // Burn the lamport, keep `clientSeq`. The caller (`executeUndoRedo`)
        // drops the clone.
        if (!ctx.isPresenceOnlyChange()) {
          this.changeID = ctx
            .getNextID()
            .setClientSeq(this.changeID.getClientSeq());
        }
        throw err;
      }
    } finally {
      // Detach the handlers whether or not either pass succeeded, so a later
      // execution of these operations cannot re-point anything and the
      // closures do not keep the popped entry alive.
      for (const splitOp of splitOps) {
        splitOp.onSplitTicketConsumed();
      }
    }
    const { operations, opInfos, reverseOps } = executed;
    const reverse = ctx.getReversePresence();
    if (reverse) {
      reverseOps.push({ type: 'presence', value: reverse });
    }

    // Now that the operations have run, re-point the history stacks at the
    // ids this entry actually minted. Deferred to here because the new
    // reverse ops are built from the executed state, so they already carry
    // the new ids and must not be pushed before this runs.
    //
    // Two things can still make a collected pair false, and both are only
    // knowable now. `change.execute` skips an operation whose target element
    // was removed during undo/redo, so it minted nothing at all. And a pair
    // is collected as the split takes its ticket on WHICHEVER execution runs
    // first -- the clone -- while the stacks have to follow the root: the
    // clone and the root are separate trees, so a split that crossed a level
    // in the clone can stop short of it in the root, leaving a pair naming a
    // ticket no node in the root ever received. Check the index against what
    // the root pass reports consuming (`getConsumedSplitTicketCount`, reset
    // per execution, so it describes the root pass here). Re-issued content
    // ids carry -1: those are minted unconditionally, once, up front.
    for (const { op, prev, curr, ticketIndex } of pending) {
      if (!operations.includes(op)) continue;
      if (ticketIndex >= 0 && ticketIndex >= op.getConsumedSplitTicketCount()) {
        continue;
      }
      this.internalHistory.reconcileTreeNodeID(
        op.getParentCreatedAt(),
        prev,
        curr,
      );
    }

    // And then what the TREE says the split re-created, the same signal the
    // local `update()` and remote `applyChangeInternal` paths re-point from.
    // The pairs above come from the reverse op, which names the merge IT
    // reverses; the split as executed can reverse one this entry never knew
    // about -- a peer merged two blocks while the undo sat on the stack, and
    // the redo of an unrelated split separates them again. Neither signal
    // subsumes the other: the tree derives a pair only where the merge left
    // `mergedFrom` stamps behind (`CRDTTree.mergeSourceOf`), which a split's
    // own boundary-deletion undo does not. Both are applied, in that order;
    // a pair the other already handled sweeps nothing and costs nothing.
    for (const op of operations) {
      if (op instanceof TreeEditOperation) {
        for (const [prev, curr] of op.getSplitRecreatedIDs()) {
          this.internalHistory.reconcileTreeNodeID(
            op.getParentCreatedAt(),
            prev,
            curr,
          );
        }
      }
    }

    if (reverseOps.length) {
      if (isUndo) {
        this.internalHistory.pushRedo(reverseOps);
      } else {
        this.internalHistory.pushUndo(reverseOps);
      }
    }

    // NOTE(chacha912): When there is no applied operation or presence
    // during undo/redo, skip propagating change remotely.
    //
    // The test is whether an operation RAN, not whether it produced an
    // `OpInfo`. Those differ: a style may change CRDT state without anything
    // an editor could render, because `canStyle` admits a node another client
    // removed concurrently and a tombstone has no index to report. Gating on
    // `opInfos` dropped such a reverse style on the floor -- it mutated this
    // replica and never reached the others, which is the divergence the
    // `canStyle` contract exists to prevent, reintroduced through undo.
    //
    // `change.execute` omits an operation whose target was removed while the
    // undo was pending (its result is undefined), so that case is still gated
    // out. What gets through instead is a change that ran and showed nothing,
    // which costs a redundant change on the wire and never a silent drop.
    if (!change.hasPresenceChange() && !operations.length) {
      return;
    }

    this.localChanges.push(change);
    this.changeID = ctx.getNextID();
    this.localChangeObserver.next();
    const events: DocEvents<P> = [];
    // Gated on the operations that RAN, not on the `OpInfo`s they produced: an
    // undo can run and show nothing (a reverse style on a node a peer removed,
    // a Tree restore whose nodes all land under a removed ancestor), and the
    // change is still queued above and still consumes a `clientSeq`. Keep
    // the established public event; persistence uses the separate queue signal.
    if (operations.length) {
      events.push({
        type: DocEventType.LocalChange,
        source: OpSource.UndoRedo,
        value: {
          message: change.getMessage() || '',
          operations: opInfos,
          actor: actorID,
          clientSeq: change.getID().getClientSeq(),
          serverSeq: change.getID().getServerSeq(),
        },
        rawChange: this.isEnableDevtools() ? change.toStruct() : undefined,
      });
    }
    if (change.hasPresenceChange()) {
      const presenceEvent = this.reconcilePresence(
        actorID,
        prev,
        OpSource.UndoRedo,
      );
      if (presenceEvent) {
        events.push(presenceEvent);
      }
    }
    this.publish(events);
  }
}
